import { LitElement, html, css, CSSResultGroup, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PluginInputChangeHandler, PluginStepState } from '@ixit/dof-core';
import {
    MACHINE_MEASURE, SETUP_MEASURE, extraMeasureKeysOf, measureLabel, measureValue,
} from '@ixit/dof-core';
import {
    canAddSegment,
    canRemoveSegment,
    displaySegments,
    isSingleSegmentTool,
    segmentCap,
} from './segment-cap';
import {
    blankSegment,
    type NamePart,
    type SegmentInput,
    type SegmentToleranceResult,
    type StepdrillItemState,
    type StepdrillMeasurementMode,
} from './stepdrill-types.js';
import { compactSv, deviationSv, formatSv, parseDecimal, preciseSv } from './stepdrill-format.js';
import { readSegmentTones } from './segment-tones.js';
// Side-effect import — registers the shared <ixit-rotary-tool-silhouette> family component
// (moved from this plugin to dof-core 2026-06-19). dof-core is @umbraco-cms-free, so this is
// safe in the standalone preview bundle too (see memory feedback_plugin_bundles_no_umbraco_imports).
import '@ixit/dof-core';

/** Segment-input field key ↔ silhouette data-measure. The drawing tags annotations with
 *  diameter/length/angle (radius is reserved — RadiusDim isn't drawn yet). `approachAngle`
 *  (the shank cell key) also maps to `angle` so focusing the shank row highlights its arc. */
const KEY_TO_MEASURE: Record<string, string> = {
    diameter: 'diameter', length: 'length', incomingAngle: 'angle', incomingCornerRadius: 'radius',
    approachAngle: 'angle',
};
const MEASURE_TO_KEY: Record<string, string> = {
    diameter: 'diameter', length: 'length', angle: 'incomingAngle', radius: 'incomingCornerRadius',
};

/** Which segment-input field a counted part is ABOUT — a diameter part is the row's Ø, a cone run is
 *  its angle. Routed through KEY_TO_MEASURE (above) rather than naming the silhouette's measure
 *  directly, so the part strip can't drift from the focus-highlight path. */
const PART_KIND_TO_KEY: Record<string, string> = {
    diameter: 'diameter', cone: 'incomingAngle',
};

/** Row sentinel for the DERIVED shank chamfer — a cone no drawn row carries (NamePart.ShankRow on
 *  the server). Mapped to the preview's own shank row so hovering the part lights it up.
 *
 *  A SERVER-payload value, never a DOM one: the markup's row indices are `0…n-1` for the drawn
 *  segments and SHANK_INDEX for the shank row, nothing else. There used to be a tip row at
 *  `data-segment-index="-1"` beside this, and the two −1s had to be kept apart by hand — that row is
 *  gone (stegborr-segmentmodellen 19 — spetsen är segment 1:s kon: the tip is row `#1`'s cone), so
 *  this constant is mapped to SHANK_INDEX before any tag is built and nothing else reads a −1. Named
 *  rather than inlined so the next reader doesn't have to work that out from a bare −1. */
const SHANK_CHAMFER_ROW = -1;

/** The row whose α is the POINT ANGLE — the first drawn segment, whose cone is the tip
 *  (stegborr-segmentmodellen 19 — spetsen är segment 1:s kon). One name for what used to be a
 *  literal 0 in five places of the point-angle cell and its reset. */
const POINT_ANGLE_ROW = 0;

/** The shank approach-angle is the one annotation outside the numbered-segment scheme: its
 *  editor row uses data-segment-index=SHANK_INDEX + data-key=SHANK_KEY, and the silhouette
 *  tags it data-seg="{alias}.shank" data-measure="angle" (StepdrillToolProfileProvider). These
 *  constants + _segTag() are the single source so focus-highlight, click-routing and the
 *  editability gate stay in sync (they drifted once — the shank highlight silently broke). */
const SHANK_INDEX = 'shank';
const SHANK_KEY = 'approachAngle';

/**
 * End-user preview for a Stepdrill item. Renders the variable-length segment list
 * with per-segment geometry inputs and lets the user add/remove segments. On every
 * change we dispatch the FULL updated segment array back through the standard
 * onInputChange handler — the runtime stores it under Inputs[item.alias] and
 * re-evaluates StepdrillEvaluator, which echoes the segments + per-op timings
 * back via item.detail. The component reads its inputs from item.detail.segments[i].input
 * so server-side echo is the single source of truth (avoids local-vs-server drift).
 *
 * Designer for the operations list lives in stepdrill-designer.ts. Theme support
 * (dark/light) and a reset-all-segments button were proposed as plugin-local
 * features but pulled out — both belong on Preview-base in dof-designer so the
 * whole preview-tool gets a consistent look + a global reset. Tracked in memory.
 */
/** The segment columns where a ZERO means ABSENCE rather than a value — so the cell may render
 *  empty and carry its declared bound in the placeholder.
 *
 *  Exactly one member today, and the engine draws the same line: `diameters[i] <= 0` is what puts a
 *  segment in `incompleteSegments`. The other three columns hold real zeros — a cone-only row has
 *  length 0, a flat shoulder has angle 0, a sharp corner has radius 0 — and blanking those would
 *  erase data while pretending to reveal absence. Widen this set only alongside a matching change
 *  in the evaluator's own reading. */
const ABSENT_AS_EMPTY = new Set(['diameter']);

/** The used pixel width of every grid column, read off a computed `grid-template-columns`. Empty
 *  when the value is not a list of lengths — `none`, a template still written in `minmax()`, or an
 *  environment that lays nothing out — and an empty answer leaves the offsets untouched rather than
 *  writing a zero over a real measurement. */
export function usedTrackWidths(computed: string): number[] {
    const parts = computed.trim().split(/\s+/);
    const widths = parts.map((p) => Number.parseFloat(p));
    return parts.length > 0 && widths.every((w) => Number.isFinite(w)) ? widths : [];
}

/**
 * Where each frozen column starts, and how wide the × column is — out of the tracks' measured
 * widths (stegborr-segmentmodellen 29).
 *
 * <p>Column 1 sits at 0 and is not written: the stylesheet's fallback says the same thing. The last
 * track is the × the row's sum stands one column in from.</p>
 */
export function frozenOffsets(tracks: number[], fixedColumns: number): Record<string, string> {
    const out: Record<string, string> = {};
    let left = 0;
    for (let i = 0; i < fixedColumns && i < tracks.length; i++) {
        if (i > 0) out[`--frz-c${i + 1}`] = `${left}px`;
        left += tracks[i];
    }
    out['--frz-remove'] = `${tracks.length > 0 ? tracks[tracks.length - 1] : 0}px`;
    return out;
}

/** Which way there is more to scroll — `undefined` when nothing does, so the seam draws no shadow
 *  on a table that fits. The 1px slack is sub-pixel track rounding, not taste. */
export function scrollEdge(scrollLeft: number, clientWidth: number, scrollWidth: number):
    'start' | 'middle' | 'end' | undefined {
    const travel = scrollWidth - clientWidth;
    if (travel <= 1) return undefined;
    if (scrollLeft <= 1) return 'start';
    return scrollLeft >= travel - 1 ? 'end' : 'middle';
}

/** Where the profile would be while a prefilled field stops the tool: the field the tool waits on when it
 *  waits, the same name the header and the spärr give, else the field that stops it; null when none does
 *  (stepdrill-preview.test.ts, "the empty profile names the field that stops the tool", "… the field the
 *  stop waits on"). */
export const stoppedProfileText = (stoppedAt?: string[] | null, waitingOn?: string[] | null): string | null => {
    if (!stoppedAt?.length) return null;
    const join = (names: string[]) => names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} och ${names[names.length - 1]}`;
    if (waitingOn?.length) return `Profil visas när ${join(waitingOn)} är ${waitingOn.length === 1 ? 'ifyllt' : 'ifyllda'}`;
    return `Profil visas när ${join(stoppedAt)} är ${stoppedAt.length === 1 ? 'ifylld' : 'ifyllda'}`;
};

/**
 * One verdict on its way to the status bar (issue 11). `blocking` is the SEVERITY — what the
 * commercial gate reads — and the leading word inside `body` is the WORDING, which differs per
 * source ("Spärrar realisering:", "Upplysning:", "Ofullständigt:") and therefore cannot be derived
 * from the flag. Named rather than written inline at each of the three sources, so a fourth cannot
 * quietly invent a fourth shape.
 */
interface StatusNotice {
    blocking: boolean;
    body: TemplateResult;
}

@customElement('ixit-stepdrill-preview')
export class IxitStepdrillPreview extends LitElement {
    /** ItemState from backend — includes detail with segments + totals. */
    @property({ attribute: false }) item!: StepdrillItemState;

    /** The consumption context, as dof-core declares it for every renderer: the stepAlias for the
     *  input-change dispatch, `defaultProjection` (the customer-facing view defaults the silhouette
     *  to the hole), and `customerMode` (this leaf's own author affordances come off — see the
     *  export button in render). Imported rather than re-declared locally: a private literal drifts
     *  from the contract silently, and the drift shows up as a member that is quietly always
     *  undefined. `name` is host-supplied and not on the shared type. */
    @property({ attribute: false }) state!: Partial<PluginStepState> & { name?: string };

    /** Standard handler that bubbles up through preview-base to the configurator. */
    @property({ attribute: false }) onInputChange!: PluginInputChangeHandler;

    /**
     * Local draft of the segment list. Mirrors `_serverSegments()` but is the
     * source of truth for rendering DURING active editing — without it the
     * server's echo of the last-committed value races user keystrokes and
     * overwrites what the user just typed (e.g. typing "123" sees "1" pop back
     * mid-stream after the dispatch for "1" round-trips). Synced from server
     * only when segment COUNT changes (add/remove) or `item.alias` changes —
     * intra-segment value updates are local-only until the debounced dispatch.
     */
    @state() private _draft: SegmentInput[] | null = null;

    /**
     * The last segment list this component PUSHED to the server (set in `_dispatch`, and whenever we
     * adopt the server value). Lets `updated()` tell our OWN echo — keep the draft, it may hold newer
     * in-flight keystrokes — apart from a value changed by something ELSE (a preset applied via the
     * host picker): when the server echo differs from what we last sent, the change is external and we
     * adopt it. Without this, a preset whose seed has the same ROW COUNT as the current draft reaches
     * the server + silhouette but never the visible cells.
     */
    private _lastSent: SegmentInput[] | null = null;

    /** Pending dispatch handle so we can coalesce keystrokes within 200ms. */
    private _dispatchTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Raw input strings currently being typed, keyed by "{segmentIndex}:{fieldKey}".
     * Populated on @input, cleared on @blur. While present, the input renders
     * the raw string instead of the formatted draft value — keeps trailing
     * decimals and intermediate states from being clobbered by the
     * re-format-on-render path (e.g. typing "16,034" while the formatter
     * truncates to "16,03" two decimals).
     */
    @state() private _editing: Map<string, string> = new Map();

    /** Debounce timer for the standalone shank approach-angle override dispatch. */
    private _approachTimer: number | null = null;

    /** The approach override as last TYPED, shown after blur until a fresh server echo lands — so
     *  the cell doesn't flash the previous value in the gap before re-eval. Three states on
     *  purpose: `undefined` = no local draft (read the persisted override), `null` = the user
     *  cleared the cell (inherit — even while the old override is still in the host's dictionary
     *  for the 200 ms before dispatch), a number = typed. Back to `undefined` on the next echo,
     *  unless still mid-edit OR still waiting for `_approachTimer` — until the debounce has handed
     *  the keystroke to the host, this draft is the ONLY copy of it (see willUpdate). */
    private _approachDraft: number | null | undefined = undefined;

    /** Is the status bar showing its sentences? Collapsed by default: the bar's job is to be
     *  ALWAYS THERE (so its height is reserved and nothing moves when a verdict comes or goes),
     *  and auto-opening on a new verdict would be the jump it exists to remove. */
    @state() private _statusOpen = false;

    /** Segment tag + measure whose silhouette annotations are highlighted. Set when a
     *  segment's input is focused, cleared when focus leaves the segment list. Passed to
     *  <ixit-rotary-tool-silhouette>: activeSeg lights the segment, activeMeasure accents the
     *  exact value (the angle/Ø/length you're editing). */
    @state() private _activeSeg = '';
    @state() private _activeMeasure = '';

    /**
     * Segment tag → the CSS colour of that segment's band in the silhouette
     * (stegborr-segmentmodellen/18). Read off `item.silhouetteSvg` once per DISTINCT payload — the
     * SVG is the ONLY palette; see segment-tones.ts — and painted behind the row number by
     * `_rowTone`. `_lastTonesSvg` is the string the map was built from: `item` changes on every
     * evaluation, but the drawing only changes when the geometry does, and a rescan of an identical
     * string would be the same answer at the cost of a pass over every rect.
     */
    private _segmentTones = new Map<string, string>();
    private _lastTonesSvg: string | undefined;

    /** data-segment-index → the server's ProfileSegment.Tag: numbered segments are `.seg{i}`,
     *  the shank row is `.shank`. Row `#1` (index 0) is `.seg0`, whose band already reaches the
     *  apex — the tip is that segment's cone, not a row of its own. */
    private _segTag(idx: string): string {
        const alias = this.item?.alias ?? '';
        return idx === SHANK_INDEX ? `${alias}.${SHANK_INDEX}` : `${alias}.seg${idx}`;
    }

    /** Focus entered a segment field → highlight that segment + the specific measure. The
     *  row carries data-segment-index; the cell carries data-key. Tag matches the server's
     *  ProfileSegment.Tag convention via _segTag. */
    private _onSegmentFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement | null;
        const idx = (target?.closest?.('[data-segment-index]') as HTMLElement | null)?.getAttribute('data-segment-index');
        this._activeSeg = idx == null ? '' : this._segTag(idx);
        const key = (target?.closest?.('[data-key]') as HTMLElement | null)?.getAttribute('data-key') ?? '';
        this._activeMeasure = this._activeSeg ? (KEY_TO_MEASURE[key] ?? '') : '';
    };

    /** Focus left a segment field. Keep the highlight if focus moved to another segment
     *  field (the next focusin updates it) — only clear when leaving the segment list,
     *  so tabbing between a segment's fields doesn't flicker. */
    private _onSegmentBlur = (e: FocusEvent) => {
        const next = e.relatedTarget as HTMLElement | null;
        if (next?.closest?.('[data-segment-index]')) return;
        this._activeSeg = '';
        this._activeMeasure = '';
    };

    /** Locate the editable uui-input for a (seg, measure), or null when it doesn't map to a
     *  field (shank / tip / total / non-mappable measure). Shared by the click router and the
     *  editability gate so both agree on what "editable" means. */
    private _resolveMeasureField(seg: string, measure: string): HTMLElement | null {
        // Shank approach angle lives in the dedicated shank row, not a numbered segment.
        if (seg.endsWith(`.${SHANK_INDEX}`)) {
            return measure === 'angle'
                ? this.renderRoot.querySelector<HTMLElement>(`[data-segment-index="${SHANK_INDEX}"] [data-key="${SHANK_KEY}"] uui-input`)
                : null;
        }
        const segMatch = seg.match(/\.seg(\d+)$/);
        const key = MEASURE_TO_KEY[measure];
        if (!segMatch || !key) return null;
        return this.renderRoot.querySelector<HTMLElement>(
            `[data-segment-index="${segMatch[1]}"] [data-key="${key}"] uui-input`);
    }

    /** Editability gate handed to the silhouette: a measure is editable iff it belongs to a
     *  user segment (index in range) and maps to a segment input field. Keeps the silhouette
     *  geometry-agnostic — the field mapping lives here. */
    private _isMeasureEditable = (seg: string, measure: string): boolean => {
        // Shank approach angle is editable when a shank exists (terminal Ø > 0).
        if (seg.endsWith(`.${SHANK_INDEX}`)) {
            return measure === 'angle' && Number(this.item?.detail?.terminalDiameter ?? 0) > 0;
        }
        const m = seg.match(/\.seg(\d+)$/);
        if (!m || this._segmentsFromFormulas) return false;
        const idx = Number(m[1]);
        return idx >= 0 && idx < this._segments.length && measure in MEASURE_TO_KEY;
    };

    /** Reverse direction: clicking a measure in the silhouette focuses its editor field AND
     *  selects the current value, so the user can type a replacement straight away. */
    private _onMeasureClick = (e: CustomEvent<{ seg: string; measure: string }>) => {
        const field = this._resolveMeasureField(e.detail?.seg ?? '', e.detail?.measure ?? '');
        if (!field) return;
        field.focus?.();
        // uui-input wraps a native <input> in its shadow root; select on the next frame
        // (after focus has settled) so the value is highlighted for immediate overtyping.
        requestAnimationFrame(() => {
            const native = field.shadowRoot?.querySelector<HTMLInputElement>('input, textarea');
            if (native) native.select();
            else (field as unknown as { select?: () => void }).select?.();
        });
    };

    /** Dispatch bubbling event for preview-base to handle: it has _contentId +
     *  inputs and knows how to POST to the auth-gated export route + trigger
     *  browser download. Plugin stays oblivious to fetch / auth / blob plumbing.
     *
     *  Not reachable in slutkund-läge: the button that dispatches this is not rendered
     *  (`state.customerMode`), because its route requires an Umbraco login and on the public page
     *  it would be a 401 with a download icon. preview-base also stops listening for the event —
     *  two halves of one decision, since a plugin we do not ship could dispatch it anyway. */
    private _exportStep = () => {
        this.dispatchEvent(new CustomEvent('dof-export-request', {
            detail: { itemAlias: this.item.alias, format: 'step' },
            bubbles: true,
            composed: true,
        }));
    };

    /** Display value for a numeric segment input. Renders the raw user-typed
     *  string if the cell is currently being edited; otherwise renders the
     *  formatted draft value. */
    private _cellValue(i: number, key: keyof SegmentInput, current: number): string {
        const editing = this._editing.get(`${i}:${String(key)}`);
        if (editing !== undefined) return editing;
        // An untouched Ø renders EMPTY rather than "0,00", so the field can carry its guidance in
        // the placeholder — and because "0,00" was itself a small lie: it reads as a diameter
        // somebody entered, when it means nobody has.
        //
        // Scoped to `diameter` on purpose. A zero is real data in the other three columns: a
        // cone-only row genuinely has length 0, a flat shoulder genuinely has angle 0, and a sharp
        // corner genuinely has radius 0. Blanking those would erase values, not reveal absence. The
        // engine draws the same line — `diameters[i] <= 0` is what makes a segment incomplete.
        // (Row 1's α is the exception, and it does not come through here: 0 there means INHERIT,
        // and `_renderPointAngleCell` shows the inherited value as the placeholder.)
        if (ABSENT_AS_EMPTY.has(String(key)) && current === 0) return '';
        return formatSv(current);
    }

    private _onNumberBlur(i: number, key: keyof SegmentInput) {
        return () => {
            const cellKey = `${i}:${String(key)}`;
            if (!this._editing.has(cellKey)) return;
            const next = new Map(this._editing);
            next.delete(cellKey);
            this._editing = next;
        };
    }

    static styles: CSSResultGroup = css`
        :host {
            container-type: inline-size;
            /* Two names: the plugin's own responsive rules query "stepdrill"; the shared
               silhouette (dof-core) queries "rotary-preview". Both resolve to this host. */
            container-name: stepdrill rotary-preview;
            display: block;
            margin-top: 12px;
            /* The α track, defined ONCE — two grid templates below lay out the same column, and a
               width edited in one of them is a column that shifts on a one-diameter tool and nowhere
               else. It is the widest input column because row #1's field holds three things, not
               one: 20px of 2α marker in uui-input's prepend slot (stegborr-segmentmodellen 19 —
               spetsen är segment 1:s kon), the 60px uui-input floors its own field at, and the 14px
               reset glyph that appears the moment the angle is overridden — plus the cell's own
               2×5px padding. Measured in the browser, all four. Below that sum the glyph was drawn
               outside the cell and the author could not hand an overridden angle back.
               The last 1.8px of the glyph still reaches past the cell at this width, and 126px is
               what it takes to bring that to zero — 22px of the table's width for two device pixels
               that land in the next cell's padding. It is paid for by the paint order instead: the
               frozen block stacks right-to-left, so the glyph is drawn whole. */
            --track-angle: 104px;
        }
        /* DESKTOP: cells flatten into the parent grid; cell-labels hidden. .segment-card wraps
           each segment's row but is also display:contents here, so the grid layout sees through to
           the row underneath — overridden in card-mode, where it becomes the card itself. */
        .cell, .segment-card {
            display: contents;
        }
        .cell-label {
            display: none;
        }

        /* A cell that breaks a rule. uui-input[error] paints a red border, which on its own is a
           colour-only signal — so the SHAPE carries it too: a dashed outline survives greyscale
           and red/green colour blindness alike (Ixit.Dof/CLAUDE.md). The value itself is left
           entirely alone; marking a cell must never stop it being edited.

           The dash matters MORE since the de-chroming (issue 11): the cell no longer has a border
           of its own for the error colour to repaint, so the outline is now the only edge the mark
           draws — which is exactly the carrier that survives greyscale. The tint is a colour-mix
           over transparent, never a light hex, so it follows the theme. */
        .cell uui-input[error] {
            outline: 2px dashed var(--uui-color-danger);
            outline-offset: -1px;
            background: color-mix(in srgb, var(--uui-color-danger) 8%, transparent);
        }
        /* An ADVISORY break must be tellable from a blocking one on the CELL too, not only in the
           status bar — the acceptance line says so, and until now every break painted the same
           danger dash. Dotted against dashed is a shape difference, so it survives greyscale;
           the hue only reinforces it. */
        .cell[data-severity='advisory'] uui-input[error] {
            outline-style: dotted;
            outline-color: var(--uui-color-warning);
            background: color-mix(in srgb, var(--uui-color-warning) 8%, transparent);
        }
        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 8px;
            flex-wrap: wrap;
        }
        .header-left {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1 1 auto;
            min-width: 0;
        }
        .alias {
            font-size: 14px;
            font-weight: 600;
            color: var(--uui-color-text, #1f2937);
        }
        .mode-select {
            font-size: 12px;
            min-width: 200px;
        }
        .mode-select-native {
            font-size: 12px;
            padding: 4px 6px;
            border: 1px solid var(--uui-color-border, #d1d5db);
            border-radius: 4px;
            background: var(--uui-color-surface, #ffffff);
            color: var(--uui-color-text, #1f2937);
            min-width: 200px;
        }
        .empty {
            padding: 12px;
            color: var(--uui-color-text-alt, #6b7280);
            font-style: italic;
            border: 1px dashed var(--uui-color-border, #d1d5db);
            border-radius: 4px;
            text-align: center;
        }
        .empty-guide {
            padding: 16px 20px;
            border: 1px dashed var(--uui-color-border, #d1d5db);
            border-radius: 6px;
            background: var(--uui-color-surface-alt, #f9fafb);
            margin-bottom: 8px;
        }
        .empty-guide-title {
            font-size: 14px;
            font-weight: 600;
            color: var(--uui-color-text, #1f2937);
            margin-bottom: 10px;
        }
        .empty-guide-bullets {
            margin: 0 0 14px 0;
            padding-left: 20px;
            color: var(--uui-color-text-alt, #6b7280);
            font-size: 13px;
            line-height: 1.6;
        }
        .empty-guide-bullets li {
            margin-bottom: 4px;
        }
        .empty-guide-bullets strong {
            color: var(--uui-color-text, #1f2937);
            font-weight: 600;
        }
        .empty-guide-example {
            border-top: 1px solid var(--uui-color-border, #e5e7eb);
            padding-top: 10px;
        }
        .empty-guide-example-title {
            font-size: 11px;
            font-weight: 600;
            color: var(--uui-color-text-alt, #6b7280);
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin-bottom: 6px;
        }
        .empty-guide-example-row {
            display: flex;
            align-items: baseline;
            gap: 8px;
            font-size: 12px;
            line-height: 1.5;
            color: var(--uui-color-text-alt, #6b7280);
        }
        .empty-guide-example-arrow {
            color: var(--uui-color-text-alt, #9ca3af);
            flex-shrink: 0;
        }
        .empty-guide-example-value {
            font-family: var(--uui-font-monospace, monospace);
            color: var(--uui-color-text, #1f2937);
            background: var(--uui-color-surface, #ffffff);
            padding: 1px 6px;
            border-radius: 3px;
            border: 1px solid var(--uui-color-border, #e5e7eb);
        }
        /* Single grid for header + all segment rows so column widths resolve
           once across the entire table — guarantees header text sits above the
           input it labels. row-children use display: contents to flatten into
           the parent grid; align-items: center vertically aligns every cell
           against the UUI input boxes which are the tallest items. */
        .segments-grid {
            /* Grid fills parent width; the Resultat column flexes (1fr) so it
               absorbs any space left over after the input cells hit their
               max. Without the 1fr the remaining space sat empty on
               the right, which read as "Resultat+× take 50% of the row".
               Mobile-responsive layout (stacked cards per segment) is a
               follow-up — today this works ~480px and up.

               COLUMN MINIMA ARE NOT TASTE (issue 11). uui-input's own shadow sizes its native input
               at min-width:60px and does not clip it, so a track too narrow for that lets the
               field's right edge, and with it the right-aligned digits, out over the next column.
               68px is where the spill reaches exactly zero with the 5px padding these cells carry —
               measured in the browser, one swept px at a time (stegborr-segmentmodellen 29; the 64
               that stood here was the same sum without the padding). Every column holding a
               uui-input is floored there, and the readonly Kona column is the only narrow one.

               Which also sets where the table STOPS being a table: 24+104+68+68+122+46+68+130 plus
               the remove column is ~650 px (the Tol. column added its own in
               stegborr-segmentmodellen 17 — toleransnivåer, and α grew to hold the reset glyph in
               29), and the silhouette takes 320 beside it. Under ~970 px of host the table no
               longer fits — it SCROLLS there rather than clips, which is what the frozen columns
               and the seam's shadow are for; the card breakpoint stays at 900, where the
               table stops being readable at all.

               The column-gap is 0 on purpose: the columns are separated by thin RULES drawn on the
               cells (see below), the way a drawing table separates them — a gap plus a rule reads
               as two separations. */
            /* Each fixed track is a property of its own, opening at a FLOOR that is stated once,
               because a width written twice is two numbers that drift.
               The index track sizes to its CONTENT and carries no floor at all: the shank row's cell
               says "Skaft", which needs 43px at this font, and the fixed 24px cut it to two letters.
               Neither way of writing a floor survives here, measured in the browser: a
               minmax(24px, auto) track stands at its minimum for ever once the operations overflow
               the grid, because a track only grows toward its maximum out of FREE space, and a
               min-width on the cell replaces the item's automatic minimum size instead of raising
               it, which pins the track at 24 whatever the cell says. */
            --trk-idx: auto;
            --dia-floor: 68px;
            --tol-floor: 68px;
            --len-floor: 122px;
            --kona-floor: 46px;
            --r-floor: 68px;
            --trk-dia: minmax(var(--dia-floor), 78px);
            --trk-tol: minmax(var(--tol-floor), 78px);
            --trk-len: minmax(var(--len-floor), 150px);
            --trk-kona: minmax(var(--kona-floor), 68px);
            --trk-r: minmax(var(--r-floor), 78px);
            /* The × column sizes to its button, frozen columns or not — the right block's offset is
               measured off the track rather than bought by nailing the track to a number. */
            --trk-remove: auto;
            display: grid;
            /* Overridden inline once the operations are known (_opsColumnTrack); this is the shape
               without them, and the fallback if a render ever reaches here without the style.
               Tracks: # · α · Ø · Tol. · L · Kona · r · [operations] · Resultat · remove. The Tol.
               column sits beside the Ø it qualifies (stegborr-segmentmodellen 17 — toleransnivåer);
               _fixedColumnCount has to say the same number of tracks as stand before the operations.
               The α track is --track-angle, defined on :host — it is wider than the rest and it is
               the same width in both templates, so it is stated once. */
            grid-template-columns:
                var(--trk-idx) var(--track-angle) var(--trk-dia) var(--trk-tol) var(--trk-len)
                var(--trk-kona) var(--trk-r)
                var(--ops-cols, ) minmax(130px, 1fr) var(--trk-remove);
            /* Operations widen the row without limit, so the TABLE scrolls, never the page. */
            overflow-x: auto;
            column-gap: 0;
            row-gap: 2px;
            align-items: center;
        }
        /* A one-diameter tool has no TRANSITION, so the two columns that describe one are gone
           (stegborr-segmentmodellen/13). The α column stays: it does double duty — on step 1 it IS
           the point angle, and on the shank row it is the approach angle. Both are real on a drill
           with a single diameter, which is why this drops two columns and not three.
           ONLY the template is overridden here, and it is overridden on the GRID — not on .row.
           The rows are display: contents (see .cell, .segment-card above), so they have no box and
           no grid formatting context: a grid-template-columns on .row.no-transition is dead CSS,
           which is exactly what it was until 2026-09-09. The gaps and the alignment stay on .row,
           where every row reads them — splitting them out took them from the UNCAPPED grid, which
           is every stegborr that exists.
           (No backticks in this comment: the block is inside a css tagged template and one backtick
           closes it. Second time today in this file.) */
        .segments-grid.no-transition {
            grid-template-columns:
                var(--trk-idx) var(--track-angle) var(--trk-dia) var(--trk-tol) var(--trk-len)
                var(--ops-cols, ) minmax(130px, 1fr) var(--trk-remove);
        }
        /* A Tol. cell on a row where NEITHER feature drills at the set depth: the tolerance describes
           a surface that never touches the hole wall, so the cell is DIMMED — never hidden, never
           disabled (Andreas: hindra inte redigering). Opacity is a second carrier, not the only one:
           the cell's title says why in words, and data-tolerance-applies carries it for the DOM.
           Reads the theme's own text colour through the input, so it holds in dark mode too. */
        .row.body > .cell[data-tolerance-applies="false"] > uui-input {
            opacity: 0.55;
            font-style: italic;
        }
        /* ── The grid as a DRAWING TABLE, not a form (issue 11) ────────────────────────────
           Five bordered input boxes per row put twelve edges around every segment; the CHROME,
           not the number of rows, is what made a number matrix feel like a consumer form. The
           boxes stay <uui-input> (house rule) — only its DOCUMENTED custom properties change, so
           the error state, the focus outline and the value handling the component owns are all
           untouched. Precedent: data-peek-base.ts already shrinks --uui-input-height this way. */
        .segments-grid uui-input {
            --uui-input-height: 22px;
            --uui-input-border-width: 0;
            --uui-input-border-radius: 0;
            --uui-input-background-color: transparent;
            /* --uui-input-padding is uui-input's OWN knob, declared on its :host rather than in the
               published cssprop list — worth knowing, because a UUI upgrade that renamed it would
               show up as roomier cells and nothing else. Nothing but the inner padding rides on it. */
            --uui-input-padding: 0 4px;
            /* Decimals line up column-wise: same-width digits, and the number ends at the same
               place in every row. This is the whole reason the minima above exist. */
            font-variant-numeric: tabular-nums;
            text-align: right;
            font-size: 12px;
            /* WHAT MAY BE TYPED IN, said twice and never in colour (Andreas 2026-09-03: the
               input boxes were hard to tell from the cells you may not write in — a 16% hairline
               was carrying that distinction on its own).
                 · a faint FILL, so an editable cell reads as a slot rather than as text
                 · a stronger underline, so the slot has a floor
               Both survive greyscale and red/green colour blindness, which a tinted border would
               not. A read-only cell has neither, and needs no mark of its own: absence is the
               signal, and it keeps the derived figures at full contrast for reading. */
            --uui-input-background-color: color-mix(in srgb, currentColor 5%, transparent);
            /* Drawn with an inset shadow rather than a border because the border WIDTH is the
               custom property we just zeroed — re-adding one there would undo the de-chroming.
               A property of its own because the frozen block casts its seam shadow on the same
               element, and a second box-shadow declaration REPLACES this one rather than adding
               to it — the underline would silently go missing on every frozen input cell. */
            --cell-underline: inset 0 -1px 0 color-mix(in srgb, currentColor 34%, transparent);
            box-shadow: var(--cell-underline);
        }
        /* Hover says the same thing a third way, on the cell the pointer is over. */
        .segments-grid uui-input:hover {
            --uui-input-background-color: color-mix(in srgb, currentColor 9%, transparent);
        }
        /* Focus rides the UNDERLINE property rather than a box-shadow of its own: on a frozen cell
           box-shadow belongs to the seam rule, which weighs far more than this selector and would
           simply keep the resting underline. Written into the property the seam rule composes, the
           ring lands on a frozen input and a scrolling one alike.
           uui-input's own outline ring is pulled INSIDE the box: drawn outside, the frozen cells'
           opaque grounds overlap it, and any fixed z-order between neighbours hides one side. */
        .segments-grid uui-input:focus-within {
            --cell-underline: inset 0 -2px 0 var(--uui-color-focus, currentColor);
            outline-offset: -2px;
        }
        /* Thin vertical rules instead of a gap — the drawing-table look. Applied to the GRID
           ITEMS, not to .cell: .cell is display:contents on desktop, so it has no box to put a
           border on. Reset inside the card breakpoint, where the rows are no longer columns. */
        .segments-grid .row.head > *:not(:first-child),
        .segments-grid .row.body > *:not(.idx):not(.cell),
        .segments-grid .row.body > .cell:not(.pos-cell) > uui-input,
        .segments-grid .row.body > .cell:not(.pos-cell) > .readonly-cell,
        .segments-grid .row.body > .cell.pos-cell {
            border-left: 1px solid color-mix(in srgb, currentColor 12%, transparent);
        }
        .segments-grid .row.body > *:not(.idx):not(.cell),
        .segments-grid .row.body > .cell > uui-input,
        .segments-grid .row.body > .cell > .readonly-cell {
            padding-left: 5px;
            padding-right: 5px;
            box-sizing: border-box;
        }
        /* The column rule is drawn on the CELL, so a cell shorter than its row draws a short dash
           floating in the middle instead of a line down the table — and a row whose cells differ in
           height (the index is 24px, a figure 14px, the result 16px) draws several different dashes.
           Measured in a browser: that is the "linjer som hoppar i höjdled" at column level.
           The INPUT cells are deliberately left alone: they are boxes with their own borders and
           their own height, which is the look the drawing-table pass settled on. */
        .segments-grid .row.body > *:not(.idx):not(.cell),
        .segments-grid .row.body > .cell.readonly > .readonly-cell {
            align-self: stretch;
            display: flex;
            align-items: center;
            justify-content: flex-end;
        }
        /* The bottom row had the same disease as the head, one border-side over: every cell carries
           a border-top, and unequal heights (23/17/17/17/19/3) put that rule on six different
           lines — the 3px stub at the end being the worst of them. */
        .segments-grid .row.ops-total > * {
            align-self: stretch;
        }
        /* Sharp corners on the whole table (the input radius is zeroed above). */
        .segments-grid .readonly-cell {
            font-variant-numeric: tabular-nums;
            text-align: right;
            font-size: 12px;
        }
        /* The POS cell holds the field AND the derived-position echo, so it is the one cell that
           is a BOX rather than display:contents — a second grid CHILD would shift every column
           after it (the trap that put the bound guidance in the placeholder in the first place). */
        .segments-grid .row.body > .cell.pos-cell {
            display: flex;
            align-items: center;
            gap: 4px;
            min-width: 0;
            padding-left: 5px;
            padding-right: 5px;
            box-sizing: border-box;
        }
        .segments-grid .row.body > .cell.pos-cell > uui-input {
            flex: 1 1 auto;
            min-width: 0;
            padding-left: 0;
            padding-right: 0;
            border-left: none;
        }
        /* POS→SPETS, as a small grey after-text rather than its own sub-row (issue 11, decision 2).
           A sub-row doubled every segment's height; a column of its own costs width in a table that
           is already seven columns wide; showing it only for the focused row moves, which is the
           thing the status bar just stopped doing.

           ITS WIDTH IS FIXED, and that is the load-bearing part. The field beside it flexes, so an
           echo that grew with its digit count — 9,00 against 146,09 — would move the field's right
           edge, and with it the right-aligned number: every row's decimal would land at a different
           x, which is exactly what the column was re-aligned to stop. A reading too long for the box
           is elided; the full one is in the title either way. */
        .pos-echo {
            flex: 0 0 auto;
            width: 46px;
            text-align: right;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 10px;
            font-variant-numeric: tabular-nums;
            color: color-mix(in srgb, currentColor 62%, transparent);
            white-space: nowrap;
        }
        /* A derived position outside a declared bound. The mark may not change the LAYOUT — the
           whole point of the status bar was that nothing moves — so it is paint only: a wavy
           underline here, and a dashed outline on the cell (below), which is the same shape carrier
           a broken input already uses. No glyph: an appended ⛔ widened the echo and shoved the
           field beside it. */
        .pos-echo.pos-broken {
            color: var(--uui-color-danger);
            text-decoration: underline wavy;
        }
        .segments-grid .row.body > .cell.pos-cell[data-pos-severity] {
            outline: 2px dashed var(--uui-color-danger);
            outline-offset: -1px;
            background: color-mix(in srgb, var(--uui-color-danger) 8%, transparent);
        }
        /* An advisory break differs in the SHAPE of the outline, not only its hue — dotted against
           dashed survives greyscale, which is the whole rule (Ixit.Dof/CLAUDE.md). */
        .segments-grid .row.body > .cell.pos-cell[data-pos-severity='advisory'] {
            outline-style: dotted;
            outline-color: var(--uui-color-warning);
            background: color-mix(in srgb, var(--uui-color-warning) 8%, transparent);
        }
        .head-echo {
            font-weight: 400;
            text-transform: none;
            letter-spacing: 0;
            opacity: 0.75;
        }
        .segments-grid .row {
            display: contents;
        }
        /* Column headings: small caps carrying the unit, right-aligned over the right-aligned
           numbers below them so the heading and the column read as one line. */
        .segments-grid .row.head > * {
            padding: 0 5px 4px;
            font-size: 10px;
            font-weight: 600;
            color: var(--uui-color-text-alt, #6b7280);
            text-transform: uppercase;
            letter-spacing: 0.04em;
            border-bottom: 1px solid var(--uui-color-border, #d1d5db);
            min-height: 1.2em;
            box-sizing: border-box;
            /* STRETCH, and the rule under the head depends on it. Each head cell carries its own
               border-bottom, and the grid centres cells of unequal height independently — so a
               heading that wraps (Kona (mm) is 29px in a 59px column, against 17px for its
               neighbours) put its rule lower than the rest and the divider came out as a staircase.
               Measured in a browser, not guessed. Stretching makes every cell the row's height, so
               the rules meet; the text is pushed to the bottom edge, which is where a heading above
               a table belongs anyway. */
            align-self: stretch;
            display: flex;
            align-items: flex-end;
            /* text-align no longer positions a flex line, so the three alignments are explicit. */
            justify-content: flex-end;
        }
        .segments-grid .row.head > span:first-child,
        .segments-grid .row.head > span:last-child {
            padding-left: 0;
            padding-right: 0;
        }
        /* Resultat is prose, not a figure — it keeps its left edge. Addressed by CLASS, not by
           nth-child: it used to be the seventh cell, and the operation columns splice in ahead of
           it, so the positional selector started left-aligning an arbitrary measure heading. */
        .segments-grid .row.head > .head-result {
            justify-content: flex-start;
        }
        /* The shank's derived name, muted: an alpha of the theme's own text colour, never a grey
           literal and never --uui-color-text-alt (which is not a muted colour — Ixit.Dof/CLAUDE.md).
           The italic carries the "derived, not typed" reading without the tint. */
        .segments-grid .row.shank-row .row-name {
            color: color-mix(in srgb, var(--uui-color-text) 62%, transparent);
            font-style: italic;
        }
        /* The angle-convention marker INSIDE the α field (uui-input's prepend slot): "2α" on row #1,
           whose α is the INCLUDED point angle, where every other row's α is measured from the axis.
           It sits inside the field on purpose — .cell-label is display:none on the desktop grid, so
           a marker there would only ever be seen in card mode, and an overridden row #1 would look
           exactly like row #2. Inside the field it is neither a grid child nor hidden. (No backticks
           in this comment: it lives inside a css template literal.) */
        .segments-grid .angle-mark {
            font-size: 11px;
            line-height: 1;
            padding: 0 3px 0 4px;
            align-self: center;
            color: color-mix(in srgb, var(--uui-color-text) 62%, transparent);
            white-space: nowrap;
        }
        /* The ↺ that clears an overridden angle back to inheriting. It lives in uui-input's append
           slot (see _renderInheritedAngleCell for why), so it is part of the field's own box and
           never a grid child. The glyph is the state's carrier; no colour is needed to read it. */
        .segments-grid .reset-override {
            background: transparent;
            border: 0;
            padding: 0 2px;
            margin: 0;
            font-size: 13px;
            line-height: 1;
            cursor: pointer;
            color: inherit;
            opacity: 0.75;
        }
        .segments-grid .reset-override:hover,
        .segments-grid .reset-override:focus-visible {
            opacity: 1;
            outline: 1px solid var(--uui-color-focus, currentColor);
            outline-offset: 1px;
        }
        /* The row number, and since stegborr-segmentmodellen/18 the place the SEGMENT'S TONE is
           painted — the same fill and opacity the silhouette draws that segment's band in, read off
           the SVG (segment-tones.ts), so the row and the band read as one thing. The tone is set
           inline per row because it is data, not style: it comes from the server with the drawing.
           Tinted rows are told apart by their number as well — colour never carries this alone. */
        .idx {
            font-weight: 600;
            color: var(--uui-color-text-alt, #6b7280);
            text-align: right;
            line-height: 1;
            padding: 4px 3px;
            border-radius: 3px;
            box-sizing: border-box;
        }
        uui-input {
            width: 100%;
        }
        .result {
            font-size: 12px;
            color: var(--uui-color-text-alt, #6b7280);
            padding: 0 8px;
            line-height: 1.3;
        }
        .result-total {
            white-space: nowrap;
        }
        .result-total strong {
            color: var(--uui-color-text, #1f2937);
        }
        .op-names {
            font-size: 12px;
            font-weight: 500;
            color: var(--uui-color-text-alt, #6b7280);
        }
        /* The operations' measures as COLUMNS in the segment row (operationsformen
           07 — rutnätet med härledda rader). Not a table beside it: the input and the reporting are
           one reading, and two tables sharing a row axis are two. The columns are spliced into the
           shared template — see _opsColumnTrack. */
        /* The UPPER head row carries no rule of its own: the rule for every head cell already gives
           it a border-bottom, and letting the operation row keep that drew a second full-width line
           above the measures — the divider then read as sitting in two places at once. The only mark
           this row needs is the one that GROUPS, so the name keeps a left edge and nothing else.
           (No backticks in this comment: the block is inside a css tagged template and one backtick
           closes it. THIRD time in this file — the two earlier notes say the same thing.) */
        .segments-grid .row.ops-head > * {
            border-bottom: none;
            padding-bottom: 1px;
        }
        .segments-grid .row.ops-head > .ops-head-name {
            justify-content: center;
            text-transform: none;
            letter-spacing: 0;
            font-size: 11px;
            border-left: 1px solid var(--uui-color-border, #e5e7eb);
        }
        /* The blanks either side of the operation block must not draw the column rule that every
           other head cell draws, or the empty span above the input columns grows an edge that lines
           up with nothing. */
        .segments-grid .row.ops-head > span:not(.ops-head-name) {
            border-left: none;
        }
        .ops-measure {
            text-align: right;
        }
        .ops-cell {
            text-align: right;
            font-variant-numeric: tabular-nums;
        }
        /* The bottom row: per-operation sums of what is shown, beside the SERVER's own grand total.
           They may legitimately disagree — a measure with no role feeds a column and no total — and
           this row is where that becomes visible. */
        .ops-total > * {
            border-top: 1px solid var(--uui-color-border, #e5e7eb);
            padding-top: 2px;
        }
        .ops-total-label {
            font-weight: 500;
            color: var(--uui-color-text-alt, #6b7280);
            text-align: right;
            padding-right: 6px;
        }
        .ops-total-cell {
            font-weight: 600;
        }
        /* Warning banner uses concrete amber-on-light-yellow for WCAG-AA contrast
           in light mode (the only mode this component supports today —
           dark-mode landing on the whole Preview tool is tracked separately). */
        /* An authoring GAP, not a verdict about the tool — which is why it keeps its own class
           rather than joining .notice: it says the block is unfinished, not that the drill is
           wrong, and it must not follow the validations when they move to a status bar.

           It painted a hard #fef3c7 with no variable behind it, so in the backoffice dark theme it
           was a bright slab in the middle of a dark grid. Same recipe as .notice now: a colour-mix
           tint over transparent follows whatever the theme puts behind it, and no light hex sits
           there waiting to become the value. */
        .ops-missing {
            padding: 10px 12px;
            margin: 6px 0 10px;
            background: color-mix(in srgb, var(--uui-color-warning) 12%, transparent);
            color: var(--uui-color-text);
            border-radius: 4px;
            border-left: 3px solid var(--uui-color-warning);
            font-size: 13px;
            font-weight: 500;
        }
        .remove {
            background: transparent;
            border: 1px solid transparent;
            color: var(--uui-color-text-alt, #6b7280);
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
            padding: 2px 8px;
            border-radius: 4px;
        }
        .remove:hover {
            color: var(--uui-color-danger, #dc2626);
            border-color: var(--uui-color-danger-emphasis, #fecaca);
        }
        /* Canonical-cylinder annotation inside the Δ-cell, only shown in
           absolute modes — visually subdued so the cone length stays primary. */
        /* An inherited value reads as the empty α's placeholder does: muted, with the title saying so. */
        .readonly-cell.inherited {
            color: color-mix(in srgb, var(--uui-color-text) 55%, transparent);
        }
        /* The word goes inside the value's box: the cell is display: contents, so a second child would
           be a grid column of its own. */
        .segments-grid .row.body > .cell.readonly > .readonly-cell.with-source {
            flex-direction: column;
            align-items: flex-end;
            justify-content: center;
        }
        .angle-source {
            font-size: 10px;
            font-weight: 500;
            white-space: nowrap;
        }
        .readonly-cell .cyl-split {
            color: var(--uui-color-text-alt, #9ca3af);
            font-size: 11px;
        }
        /* Part strip — the derivation of the part count, beside the rows it came from (issue 11).
           Tints are transparent over currentColor so the strip reads correctly in both the
           backoffice and the standalone preview, where the uui vars may be undefined. */
        .part-strip {
            display: flex;
            align-items: baseline;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 12px;
            padding: 8px 12px;
            border-radius: 4px;
            background: color-mix(in srgb, currentColor 6%, transparent);
            font-size: 14px;
        }
        .ps-label {
            opacity: 0.6;
        }
        .ps-count {
            font-weight: 700;
            font-variant-numeric: tabular-nums;
        }
        /* Parts the drill depth cut away. Dimmed AND bracketed AND struck through — never colour
           alone, and the brackets survive a reader who cannot tell the two opacities apart.
           DESCENDANT selector, not .ps-part.is-cut: that ties with .ps-part.is-f below on
           specificity and LOSES on source order, so cut cones kept their tint. Same trap CLAUDE.md
           records for small.hint beating .hint-warn.
           (No backticks in this comment — the block is inside a css tagged template, and one
           backtick here closes it. TS1128, and neither vitest nor a scoped build catches it.) */
        .ps-cut {
            opacity: 0.45;
        }
        .ps-cut .ps-part {
            background: none;
            text-decoration: line-through;
        }
        .ps-parts {
            display: inline-flex;
            align-items: baseline;
            gap: 4px;
            flex-wrap: wrap;
        }
        .ps-dot {
            opacity: 0.4;
        }
        .ps-part {
            display: inline-block;
            min-width: 1.35em;
            padding: 1px 4px;
            border-radius: 3px;
            text-align: center;
            font-weight: 600;
            cursor: default;
            background: color-mix(in srgb, currentColor 10%, transparent);
        }
        /* D vs f differ in weight, not hue — the strip must stay legible without colour. */
        .ps-part.is-f {
            font-style: italic;
            font-weight: 400;
            background: color-mix(in srgb, currentColor 5%, transparent);
        }
        .ps-part:hover {
            background: color-mix(in srgb, currentColor 22%, transparent);
        }
        /* A row carrying a corner radius: counted as a plain fas, which is UNCONFIRMED. Marked
           rather than silently assumed — see issue 11 point 4. */
        .ps-part.has-radius {
            outline: 1px dashed color-mix(in srgb, currentColor 45%, transparent);
            outline-offset: 1px;
        }
        .ps-radius {
            font-size: 0.7em;
            opacity: 0.75;
        }
        /* How many cones a collapsed run is made of. Without it an f covering two cones looks
           exactly like one, which is the thing the strip exists to make visible (issue 21).
           NB: no backticks in here — they terminate the css template literal (see memory
           feedback_backtick_in_css_template_comment). */
        .ps-run {
            font-size: 0.7em;
            opacity: 0.9;
        }

        .totals {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin-top: 12px;
            padding: 8px 12px;
            background: var(--uui-color-surface-alt, #f9fafb);
            border-radius: 4px;
            font-size: 14px;
        }
        .totals strong {
            display: block;
            font-size: 16px;
            margin-top: 2px;
        }
        .segments-grid .row.body > .cell.pos-cell.readonly > .readonly-cell {
            flex: 1;
            text-align: right;
        }
        .idx .source-mark {
            display: block;
            margin-top: 2px;
            font-size: 10px;
            font-weight: 500;
            white-space: nowrap;
        }
        .formula-note,
        .add-blocked {
            font-size: 12px;
            color: var(--uui-color-text);
        }
        .action-row {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            align-items: center;
            margin-top: 8px;
        }
        .add-btn,
        .export-btn {
            margin-top: 0;
        }
        .error {
            color: var(--uui-color-danger, #dc2626);
            padding: 8px;
            border: 1px solid var(--uui-color-danger-emphasis, #fecaca);
            border-radius: 4px;
            margin-bottom: 8px;
        }

        /* ── The status bar: this block's command line (issue 11, decision 1) ─────────────────
           The verdicts used to fall out ABOVE the grid, so one appearing or clearing pushed the
           whole table down the page — "puttar ner hela GUI:t". Down here it is the LAST thing in
           the block, it is rendered even when everything is clean, and therefore its height is
           always reserved: nothing on screen can move because a verdict came or went. Expanding
           grows into empty space below for the same reason.

           Collapsed it carries the COUNT per severity; expanded, the sentences unchanged. */
        .statusbar {
            margin-top: 10px;
            border-top: 2px solid var(--uui-color-border, #d1d5db);
            padding-top: 6px;
        }
        .sb-line {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            min-height: 26px;
            font-size: 12px;
            color: var(--uui-color-text);
        }
        /* A count chip carries the SAME three carriers as the band it summarises — glyph, left
           rule shape, and a bold word — so spärr and upplysning stay tellable apart in greyscale. */
        .sb-count {
            display: inline-flex;
            align-items: baseline;
            gap: 5px;
            padding: 2px 8px;
        }
        .sb-count .sb-n {
            font-weight: 700;
            font-variant-numeric: tabular-nums;
        }
        .sb-count .sb-kind {
            font-weight: 700;
        }
        .sb-count.blocking {
            border-left: 6px double var(--uui-color-danger);
            background: color-mix(in srgb, var(--uui-color-danger) 10%, transparent);
        }
        .sb-count.advisory {
            border-left: 3px solid var(--uui-color-warning);
            background: color-mix(in srgb, var(--uui-color-warning) 10%, transparent);
        }
        .sb-clean {
            color: var(--uui-color-text-alt, #6b7280);
        }
        .sb-spacer {
            margin-left: auto;
        }
        .sb-toggle {
            --uui-button-height: 22px;
            font-size: 11px;
        }
        /* Expanded: grows DOWNWARD into empty space. Nothing above it can move. */
        .sb-details {
            margin-top: 4px;
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        /* Notice banners, now rendered INSIDE the status bar's expanded half. An ADVISORY and a
           BLOCKING one must be tellable apart in GREYSCALE
           (Ixit.Dof/CLAUDE.md — colour may never be the only carrier), so three things differ
           besides the hue: the glyph (⚠ vs ⛔), the left rule (3px solid vs 6px double), and a
           bold word that says which it is. Any one of the three survives a monochrome print.

           NO HEX FALLBACKS on any of these, deliberately. Dark mode here is an Umbraco setting, not
           the OS's: the host stamps :host([data-theme='dark']) and the --uui-* variables inherit
           through the shadow boundary. A literal light hex behind var() is therefore not a safety
           net — it is the value that paints a white slab the moment the variable IS defined
           differently, which is the silent failure the rule exists for. Backgrounds are colour-mix
           tints over transparent, so they follow whatever the theme puts behind them. */
        .notice {
            display: flex;
            gap: 6px;
            align-items: flex-start;
            margin: 4px 0;
            padding: 6px 10px;
            border-radius: 4px;
            font-size: 12px;
            color: var(--uui-color-text);
        }

        .notice .notice-glyph {
            flex: 0 0 auto;
            line-height: 1.4;
        }

        .notice .notice-kind {
            font-weight: 700;
        }

        .notice.advisory {
            border-left: 3px solid var(--uui-color-warning);
            background: color-mix(in srgb, var(--uui-color-warning) 10%, transparent);
        }

        .notice.blocking {
            border-left: 6px double var(--uui-color-danger);
            background: color-mix(in srgb, var(--uui-color-danger) 10%, transparent);
        }

        /* Preview layout = body (table+totals+button) on the LEFT, silhouette
           side-panel on the RIGHT. Side-panel uses flex order:1 to land on
           the right of the body on desktop; the card-mode override below flips
           order:0 so on narrow hosts the silhouette sits at the TOP of the
           stack (above the segment cards). DOM order keeps the body first so
           non-flex consumers still read body to silhouette naturally. */
        .preview-layout {
            display: flex;
            gap: 16px;
            align-items: flex-start;
        }
        .preview-body {
            flex: 1 1 0;
            min-width: 0;
            order: 0;
        }
        /* Silhouette side-panel slot. The drawing is generated SERVER-side (path,
           annotations, diameter stack, angle schematics) and arrives as a complete
           SVG on item.silhouetteSvg; <ixit-rotary-tool-silhouette> just displays it.
           The parent only positions the element in the flex layout (body on the
           LEFT, silhouette on the RIGHT via order:1). The element is a flex
           container so its internal .silhouette column stretches to fill. */
        ixit-rotary-tool-silhouette {
            flex: 0 0 320px;
            align-self: stretch;
            display: flex;
            order: 1;
        }

        /* ── FROZEN COLUMNS: the measures scroll, the row does not (stegborr-segmentmodellen 28) ──
           The operations are the only part of this table that grows without limit, so they are the
           only part that may leave the screen: the row number, the cells that are typed into and the
           row's own sum stay where they are while the measures slide under them.

           The grid is the scroll port and the rows are display: contents, so sticky goes on the
           CELLS. A cell that is itself display: contents has no box, so each rule names the element
           inside it too; the offset rides on a custom property, which inherits from the cell to that
           element and is therefore written once per column.

           THE OFFSETS ARE MEASURED, not declared: --frz-c2..--frz-c7 and --frz-remove are written
           on the grid by the component out of the tracks' used widths, so the tracks keep their
           natural minmax sizing and an offset cannot disagree with the column it belongs to
           (stegborr-segmentmodellen 29 — the earlier constants were bought by collapsing every
           fixed track to its floor). Unset, every offset falls back to 0 for one frame.

           --frz-z counts DOWN to the right: a field whose content spills past its box — the α
           cell's reset glyph still does, by the 1.8px the track deliberately does not buy back —
           lands ON its right neighbour's ground rather than under it, where the neighbour's opaque
           ground cut it off. Every value is above the measures, which are not positioned at all.

           LAST in the stylesheet on purpose: the seam and the ground tie on specificity with the
           drawing-table rules above, and a tie is settled by source order — the column rule won and
           the seam was never drawn. Under the card query for the same reason it is a query of its
           own: below 900px the rows are cards, a card is not a table, and a cell frozen there would
           stick to its own card. An undo inside the card block would have to out-specify every rule
           here, and a container query adds no specificity of its own. */
        @container stepdrill (min-width: 901px) {
            .segments-grid.has-ops {
                /* The bar only when the table overflows: a bar on a table that fits promises travel
                   there is none of. Where the scrollbar is an overlay, the signal that there is more
                   to scroll is the seam's shadow, drawn only when scrollEdge finds travel. */
                overflow-x: auto;
                /* An opaque ground under every frozen cell, or the measures read through it as they
                   pass beneath. Canvas — the UA's own page colour — as the fallback rather than a
                   light hex, which would paint a white slab the day the theme goes dark. */
                --frz-ground: var(--uui-color-surface, Canvas);
                --frz-seam: color-mix(in srgb, currentColor 30%, transparent);
                /* The seam's shadow: greyscale depth, no hue at all, so it carries nothing that
                   needs colour vision to read (Ixit.Dof/CLAUDE.md). */
                --frz-shade: color-mix(in srgb, currentColor 28%, transparent);
                /* A NEUTRAL shadow as the absent state, never none: box-shadow takes a list, and a
                   shadow followed by none is not a valid list — the whole declaration falls, and
                   the seam cell's --cell-underline falls with it. (No backticks in this comment:
                   the block is a css tagged template, and one backtick closes it.) */
                --frz-cast-left: 0 0 transparent;
                --frz-cast-right: 0 0 transparent;
            }
            /* Which way there is more to scroll, said by data-scroll on the grid: a shadow falls
               INTO the moving part from the seam it is hiding measures behind. At the start
               everything still hidden lies under the RIGHT block, so that is the block that casts;
               at the end it lies under the left one. */
            .segments-grid.has-ops[data-scroll='start'],
            .segments-grid.has-ops[data-scroll='middle'] {
                --frz-cast-right: -6px 0 6px -4px var(--frz-shade);
            }
            .segments-grid.has-ops[data-scroll='middle'],
            .segments-grid.has-ops[data-scroll='end'] {
                --frz-cast-left: 6px 0 6px -4px var(--frz-shade);
            }
            /* The left block: the index and the fixed cells. Kona and r are frozen only where they
               exist — in the narrow template those child positions belong to the operations. */
            .segments-grid.has-ops .row.head:not(.ops-head) > *:nth-child(-n + 5),
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(-n + 5),
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(-n + 5):not(.pos-cell) > uui-input,
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(-n + 5):not(.pos-cell) > .readonly-cell,
            .segments-grid.has-ops:not(.no-transition) .row.head:not(.ops-head) > *:nth-child(n + 6):nth-child(-n + 7),
            .segments-grid.has-ops:not(.no-transition) .row.body:not(.ops-total) > *:nth-child(n + 6):nth-child(-n + 7),
            .segments-grid.has-ops:not(.no-transition) .row.body:not(.ops-total) > *:nth-child(n + 6):nth-child(-n + 7) > uui-input,
            .segments-grid.has-ops:not(.no-transition) .row.body:not(.ops-total) > *:nth-child(n + 6):nth-child(-n + 7) > .readonly-cell,
            .segments-grid.has-ops .row.ops-head > *:first-child,
            .segments-grid.has-ops .row.ops-total > *:first-child {
                position: sticky;
                left: var(--frz-left, 0px);
                z-index: var(--frz-z, 7);
                background: var(--frz-ground);
                border-right: var(--frz-edge, 0) solid var(--frz-seam);
                box-shadow: var(--cell-underline, 0 0 transparent), var(--frz-cast, 0 0 transparent);
            }
            /* One offset per column, set on the cell and inherited by the element inside it, out of
               the properties the component measures. The first column and the two spanning cells
               stand at 0 and say so by saying nothing. */
            .segments-grid.has-ops .row.head:not(.ops-head) > *:nth-child(2),
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(2) {
                --frz-left: var(--frz-c2);
                --frz-z: 6;
            }
            .segments-grid.has-ops .row.head:not(.ops-head) > *:nth-child(3),
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(3) {
                --frz-left: var(--frz-c3);
                --frz-z: 5;
            }
            .segments-grid.has-ops .row.head:not(.ops-head) > *:nth-child(4),
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(4) {
                --frz-left: var(--frz-c4);
                --frz-z: 4;
            }
            .segments-grid.has-ops .row.head:not(.ops-head) > *:nth-child(5),
            .segments-grid.has-ops .row.body:not(.ops-total) > *:nth-child(5) {
                --frz-left: var(--frz-c5);
                --frz-z: 3;
            }
            .segments-grid.has-ops:not(.no-transition) .row.head:not(.ops-head) > *:nth-child(6),
            .segments-grid.has-ops:not(.no-transition) .row.body:not(.ops-total) > *:nth-child(6) {
                --frz-left: var(--frz-c6);
                --frz-z: 2;
            }
            .segments-grid.has-ops:not(.no-transition) .row.head:not(.ops-head) > *:nth-child(7),
            .segments-grid.has-ops:not(.no-transition) .row.body:not(.ops-total) > *:nth-child(7) {
                --frz-left: var(--frz-c7);
                --frz-z: 1;
            }
            /* The seam: where the frozen block ends and the measures begin to move. On the LAST
               frozen column, which is r where the transition columns exist and L where they do not,
               and on the two cells that span the whole block. The shadow rides the same cells, so
               the depth is drawn exactly where the measures disappear. */
            .segments-grid.has-ops:not(.no-transition) .row.head:not(.ops-head) > *:nth-child(7),
            .segments-grid.has-ops:not(.no-transition) .row.body:not(.ops-total) > *:nth-child(7),
            .segments-grid.has-ops.no-transition .row.head:not(.ops-head) > *:nth-child(5),
            .segments-grid.has-ops.no-transition .row.body:not(.ops-total) > *:nth-child(5),
            .segments-grid.has-ops .row.ops-head > *:first-child,
            .segments-grid.has-ops .row.ops-total > *:first-child {
                --frz-edge: 2px;
                --frz-cast: var(--frz-cast-left);
            }
            /* The right block: the row's sum and the × beside it, so a row can always be read and
               removed. Resultat stands one × column in from the edge; the × itself is the edge. */
            .segments-grid.has-ops .row.body > .result,
            .segments-grid.has-ops .row.head:not(.ops-head) > .head-result,
            .segments-grid.has-ops .row > *:last-child {
                position: sticky;
                z-index: var(--frz-z, 3);
                background: var(--frz-ground);
            }
            /* The row is addressed as .row.body and not as .row, and that is not decoration: the
               drawing-table rule above draws a 1px column edge on the same cell, and at equal
               weight the seam loses — dead CSS that reads exactly like a drawn one. Measured in a
               browser. (No backticks in this comment: the block is a css tagged template, and one
               backtick closes it. The file says so four times now.) */
            .segments-grid.has-ops .row.body > .result,
            .segments-grid.has-ops .row.head:not(.ops-head) > .head-result {
                right: var(--frz-remove, 0px);
                border-left: 2px solid var(--frz-seam);
                box-shadow: var(--frz-cast-right);
            }
            .segments-grid.has-ops .row > *:last-child {
                right: 0;
                /* One step above the sum beside it (which stands at the 3 it falls back to): the ×
                   track is measured, so the two boxes meet on a fraction of a pixel, and whatever a
                   rounding leaves over belongs to the edge rather than to a sum drawn across it. */
                --frz-z: 4;
            }
            .segments-grid.has-ops .row.ops-head > *:last-child {
                border-left: 2px solid var(--frz-seam);
                box-shadow: var(--frz-cast-right);
            }
        }
        /* CARDS (<900px host): table breaks to a card grid that auto-wraps —
           2-3 cards per row when there's space, 1 card when there isn't. The
           auto-fit + minmax(280px, 1fr) is what gives this responsiveness
           without separate 1-col/2-col breakpoints.

           THE BREAKPOINT MOVED FROM 700 TO 900 with issue 11's densification, and the number comes
           out of the grid rather than out of taste: the floored columns need ~600 px (measured with
           the Tol. column, stegborr-segmentmodellen 17 — toleransnivåer) and the
           silhouette takes 320 beside them, so between 700 and 900 the table only ever "fitted" by
           letting uui-input clip the right edge off its own fields. That was invisible while the
           numbers were left-aligned and would now cut the digits. A band that cannot hold the table
           gets the cards, which read correctly at that width — rather than a table squeezed until
           it lies. */
        @container stepdrill (max-width: 900px) {
            /* Stack silhouette ABOVE the body on narrow hosts. order: 0 on
               .silhouette flips it ahead of the body which keeps order: 0 by
               default — the silhouette rides at the top of the column so the
               user sees the live profile before scrolling through the cards. */
            .preview-layout {
                flex-direction: column;
            }
            .preview-body {
                order: 1;
            }
            ixit-rotary-tool-silhouette {
                flex: 0 0 auto;
                align-self: stretch;
                /* No height cap on narrow hosts: the tool SVG is tall/portrait, so a fixed
                   max-height pinned it to the panel height and "meet" shrank it to a centred
                   sliver. Let it fill the full card WIDTH and grow as tall as its aspect ratio
                   needs (the inner svg switches to height:auto via the matching @container rule). */
                order: 0;
            }
            .segments-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                gap: 10px;
                align-items: start;
            }
            /* Each card is one grid cell holding one segment's body. */
            .segment-card {
                display: flex;
                flex-direction: column;
                gap: 0;
            }
            .row.head {
                display: none;
            }
            /* Undo the desktop drawing-table treatment: down here the columns ARE rows, so a
               vertical rule between them is a stray line and the tight paddings squash the
               label/field pairs. The de-chromed input keeps its height and its underline —
               that part reads correctly in both modes. */
            /* The :not(.pos-cell) is not decoration — it MATCHES the desktop rule's specificity.
               Without it this reset is one class lighter than the rule it undoes
               (.cell:not(.pos-cell) > .readonly-cell), so it never won and every read-only cell kept
               a stray column rule beside its label down here. Pre-existing; four more read-only rows
               per card is what made it visible. */
            .segments-grid .row.body > *:not(.idx):not(.cell),
            .segments-grid .row.body > .cell:not(.pos-cell) > uui-input,
            .segments-grid .row.body > .cell:not(.pos-cell) > .readonly-cell,
            .segments-grid .row.body > .cell.pos-cell {
                border-left: none;
                padding-left: 0;
                padding-right: 0;
            }
            .segments-grid uui-input,
            .segments-grid .readonly-cell {
                text-align: left;
            }
            /* The POS cell goes back to display:contents like every other cell (the card grid
               places label and field itself), and the echo takes its own line under the field. */
            .segments-grid .row.body > .cell.pos-cell {
                display: contents;
            }
            .pos-echo {
                grid-column: 2 / -1;
                font-size: 11px;
            }
            /* Card layout: label-left / input-right per row. One cell per row
               so the input gets full card width instead of fighting for space
               with siblings. Idx + remove form a header row spanning both cols
               via display:contents on .cell. Result spans full width below. */
            .row.body {
                display: grid;
                /* The label track GROWS to its longest label. At a fixed 58px "rund · Ställ" wrapped
                   to two lines, and a wrapped label costs a whole row — measured at 520px: 20 of 31
                   labels wrapped, and the table ran 1531px tall. Capped at 45% so a long operation
                   name cannot eat the card the value has to fit in. */
                grid-template-columns: minmax(58px, 92px) 1fr auto;
                gap: 6px 8px;
                align-items: center;
                padding: 10px 12px;
                border: 1px solid var(--uui-color-border, #e5e7eb);
                border-radius: 6px;
                background: var(--uui-color-surface, #ffffff);
            }
            .row.body > .idx {
                grid-column: 1 / 2;
                font-size: 13px;
                font-weight: 600;
                text-align: left;
            }
            .row.body > .remove {
                grid-column: 3 / 4;
                grid-row: 1;
                justify-self: end;
                margin-left: auto;
            }
            .row.body > .result {
                grid-column: 1 / -1;
                padding-top: 6px;
                border-top: 1px solid var(--uui-color-border, #f3f4f6);
            }
            /* Each cell becomes a sub-grid row: label in col 1, input/value
               spans col 2-3 to absorb the remove-col on subsequent rows. */
            .row.body > .cell {
                display: contents;
            }
            .row.body > .cell .cell-label {
                display: block;
                grid-column: 1 / 2;
                font-size: 11px;
                font-weight: 600;
                color: var(--uui-color-text-alt, #6b7280);
                text-transform: uppercase;
                letter-spacing: 0.04em;
                align-self: center;
            }
            /* A read-only cell's label names a DERIVED figure, not a field. Mixed case is narrower
               — which is what keeps it on one line — and it reads as a different kind of thing,
               which it is: nothing here is an instruction to type. */
            .row.body > .cell.readonly .cell-label {
                text-transform: none;
                letter-spacing: 0;
                font-weight: 500;
            }
            .row.body > .cell uui-input {
                grid-column: 2 / -1;
                width: 100%;
            }
            .row.body > .cell.readonly .readonly-cell {
                grid-column: 2 / -1;
                padding: 0;
                align-self: center;
            }
        }
    `;

    /** Wire-format read: pulls segments directly from server echo
     *  (`item.detail.segments`). Used as the initial source and as the
     *  re-sync source when structural shape changes (add/remove). */
    private get _serverSegments(): SegmentInput[] {
        const fromServer = this.item?.detail?.segments;
        if (!fromServer) return [];
        return fromServer.map((s) => ({
            diameter: Number(s.input?.['diameter'] ?? 0),
            length: Number(s.input?.['length'] ?? 0),
            incomingAngle: Number(s.input?.['incomingAngle'] ?? 0),
            incomingCornerRadius: Number(s.input?.['incomingCornerRadius'] ?? 0),
            tool: String(s.input?.['tool'] ?? ''),
            tolerance: String(s.input?.['tolerance'] ?? ''),
        }));
    }

    /** Display segments — prefer local draft during active editing, fall back
     *  to server echo for first paint or post-add/remove. */
    private get _segments(): SegmentInput[] {
        if (this._segmentsFromFormulas) return this._serverSegments;
        return this._draft ?? this._serverSegments;
    }

    /**
     * The block's formulas write the segment, and the customer writes none: the engine reads neither
     * her list nor `_mode` then. The row is drawn as text and nothing on the grid sends anything
     * back, because an echo of the formulas' values would arrive as the customer's own answer
     * (stepdrill-preview.test.ts, "nothing under formulas sends the list or the mode back").
     */
    private get _segmentsFromFormulas(): boolean {
        return this.item?.detail?.segmentSource === 'formulas';
    }

    /** Adopt server values into the editable draft on: first paint (no draft), a row-COUNT change
     *  (add/remove elsewhere, or a different block), OR an EXTERNAL change — the server values differ
     *  from what we last sent, i.e. the input was set by something other than this component (a preset
     *  applied via the host picker). The external case is what makes a same-row-count preset seed reach
     *  the cells, not just the silhouette. Otherwise we keep the local draft to avoid clobbering
     *  in-flight edits (the server echo of our own keystrokes equals `_lastSent`). */
    /// Indexing happens BEFORE render, not in updated(): the maps are read while rendering the
    /// cells, and updated() runs after — a render behind, so the first payload carrying a mark
    /// would draw an unmarked grid.
    protected willUpdate(changed: PropertyValues): void {
        if (!changed.has('item')) return;
        this._indexCellMarks();
        this._indexSegmentTones();
        this._indexOutcomeColumns();
        // Fresh server echo → drop the local approach-angle draft, so the shank cell reads the
        // host's persisted override (and the title the server's resolved, possibly clamped, angle)
        // instead of the last keystroke. Kept while the user is still mid-edit (raw in _editing).
        // HERE and not in updated(): the draft is not reactive state, so a clear after render would
        // leave the cell one render behind — showing the keystroke until something else redrew.
        //
        // NOT while the 200 ms send is still pending. An echo landing in that window is the answer
        // to an OLDER evaluation (the length cell edited a moment before), and the host's dictionary
        // does not carry the keystroke yet — clearing the draft here made the cell snap back to
        // "inherited" for a value the user had just typed. Once the timer has fired, the host holds
        // the value and the next echo may take over as before.
        if (!this._editing.has('shank:approachAngle') && this._approachTimer === null) this._approachDraft = undefined;
    }

    protected updated(changed: PropertyValues): void {
        // Before the early return: the frozen block's offsets belong to the grid that was just
        // drawn, whatever caused the render.
        this._measureFrozenBlock();
        if (!changed.has('item')) return;
        const server = this._serverSegments;
        const external = !this._segmentsEqual(server, this._lastSent);
        if (this._draft == null || this._draft.length !== server.length || external) {
            this._draft = server;
            this._lastSent = server;
        }
    }

    /** Redraws the measurement when the grid's own box changes — a resized panel re-sizes every
     *  track, and an offset from the previous width puts a frozen cell over its neighbour. */
    private _trackObserver: ResizeObserver | null = null;
    private _observedGrid: Element | null = null;

    /**
     * Writes the frozen block's left offsets, the × column's width and the scroll state on the grid,
     * out of what the layout actually did (stegborr-segmentmodellen 29).
     *
     * <p>Cheap enough to run after every render: one style read on one element, and only while the
     * operations are there — without them nothing is frozen and nothing scrolls.</p>
     */
    private _measureFrozenBlock(): void {
        const grid = this.renderRoot?.querySelector<HTMLElement>('.segments-grid') ?? null;
        if (grid !== this._observedGrid) {
            if (this._observedGrid !== null) this._trackObserver?.unobserve(this._observedGrid);
            this._observedGrid = grid;
            if (grid !== null) {
                this._trackObserver ??= typeof ResizeObserver === 'undefined'
                    ? null
                    : new ResizeObserver(() => this._measureFrozenBlock());
                this._trackObserver?.observe(grid);
            }
        }
        if (grid === null || !grid.classList.contains('has-ops')) return;

        const tracks = usedTrackWidths(getComputedStyle(grid).gridTemplateColumns);
        if (tracks.length > 0) {
            for (const [name, value] of Object.entries(frozenOffsets(tracks, this._fixedColumnCount))) {
                grid.style.setProperty(name, value);
            }
        }
        const edge = scrollEdge(grid.scrollLeft, grid.clientWidth, grid.scrollWidth);
        if (edge === undefined) grid.removeAttribute('data-scroll');
        else grid.setAttribute('data-scroll', edge);
    }

    /** The seam's shadow follows the scroll: which side still hides measures is a fact about
     *  `scrollLeft`, and nothing else on the row changes, so this re-measures rather than renders. */
    private _onGridScroll = () => this._measureFrozenBlock();

    /** Field-wise segment equality. Coerces because a draft may hold raw input strings while the
     *  server echo holds numbers; used only to tell an external input change from our own echo. */
    private _segmentsEqual(a: SegmentInput[] | null, b: SegmentInput[] | null): boolean {
        if (a === b) return true;
        if (a == null || b == null || a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            const x = a[i], y = b[i];
            if (Number(x.diameter ?? 0) !== Number(y.diameter ?? 0)) return false;
            if (Number(x.length ?? 0) !== Number(y.length ?? 0)) return false;
            if (Number(x.incomingAngle ?? 0) !== Number(y.incomingAngle ?? 0)) return false;
            if (Number(x.incomingCornerRadius ?? 0) !== Number(y.incomingCornerRadius ?? 0)) return false;
            if (String(x.tool ?? '') !== String(y.tool ?? '')) return false;
            // Compared like `tool`: text, verbatim. Left out, a preset that seeds a tolerance would
            // reach the server and never the cell.
            if (String(x.tolerance ?? '') !== String(y.tolerance ?? '')) return false;
        }
        return true;
    }

    private _dispatch(segments: SegmentInput[], mode?: StepdrillMeasurementMode) {
        if (this._segmentsFromFormulas) return;
        const stepAlias = this.state?.stepAlias ?? this.state?.name ?? '';
        // Mode travels WITH the values, always: a set of segment lengths is meaningless without the
        // mode it's expressed in, so we never send one without the other (the server can't otherwise
        // know how to interpret `length`). Defaults to the current echoed mode for plain value
        // edits; _onModeChange passes the NEW mode. The host's 250ms eval debounce coalesces the two
        // input keys into one POST, so the server always sees a coherent (values, mode) pair.
        const effectiveMode = mode ?? this.item?.detail?.measurementMode ?? 'Relative';
        // Record what we're sending so the next server echo reads as our own (keep the draft), not an
        // external change (adopt) — see `updated()`.
        this._lastSent = segments;
        this.onInputChange(stepAlias, this.item.alias, segments);
        this.onInputChange(stepAlias, `${this.item.alias}._mode`, effectiveMode);
    }

    /**
     * The segment CAP this configurator declares (stegborr-segmentmodellen/13, B1) — undefined for
     * no cap, which is the ordinary stegborr. Null and 0 are NOT the same thing upstream, so this
     * deliberately does not coalesce: only a real positive number is a cap.
     */
    private get _maxSegments(): number | undefined {
        return segmentCap(this.item?.detail?.maxSegments);
    }

    /** A one-diameter tool — enkelt borr or centrumborr. The surface strips itself at this. */
    private get _isSingleSegmentTool(): boolean {
        return isSingleSegmentTool(this._maxSegments);
    }

    /**
     * What the GRID draws — the real rows, or the single placeholder on a one-diameter tool
     * (stegborr-segmentmodellen/13, B2). The rule itself lives in `segment-cap.ts`, where it is
     * tested; this is the element's read of it.
     *
     * <p><b>Display only — nothing is written until the first keystroke.</b> Seeding the input on
     * open would mark the configuration changed before the customer touched anything, which is a
     * side effect the surface has no business causing. `_editField` reads THIS getter as its base
     * for the same reason: the row it draws is the row an edit creates, so the customer's first
     * value cannot fall into a list that does not have it.</p>
     *
     * <p>It also cannot look like an error while untouched, for a reason stronger than styling: the
     * server has zero rows, so there is nothing for it to mark incomplete. The emptiness the
     * customer sees is genuinely "not filled in yet" — issue 16's rule holds by construction.</p>
     */
    /**
     * Whether the grid draws the two TRANSITION columns (Kona, r).
     *
     * A one-diameter tool has no transition, so they describe nothing. The α column is NOT dropped
     * with them, and that is deliberate: α does double duty — on step 1 it IS the point angle (the
     * header's own tooltip says so) and on the shank row it is the approach angle up to the shank.
     * Both are real on a drill with one diameter. Dropping α with the others would take the point
     * angle away from every simple drill.
     */
    private get _showsTransitionColumns(): boolean {
        return !this._isSingleSegmentTool;
    }

    /**
     * Grid variant class. Belongs on the GRID CONTAINER, and is also put on the rows so their own
     * selectors can key off it.
     *
     * <p><b>The container is the one that matters, and for four days it did not get it.</b> The
     * rows are `display: contents` — that is the whole design of this table, one grid so the column
     * widths resolve once across header and body. An element with `display: contents` has no box
     * and no grid formatting context, so `grid-template-columns` on `.row.no-transition` was dead
     * CSS: the container kept the seven-track template with Kona and r while the rows emitted five
     * fixed cells. Seventeen declared tracks against fifteen filled ones, everything after the
     * fifth column two tracks off, and two operation cells drawn on top of each other. Found by
     * Andreas on test 2026-09-09 in a maxSegments=1 tool.</p>
     */
    private get _rowVariant(): string {
        return this._showsTransitionColumns ? '' : 'no-transition';
    }

    private get _displaySegments(): SegmentInput[] {
        if (this._segmentsFromFormulas) return this._segments;
        return displaySegments(this._segments, this._maxSegments, blankSegment);
    }

    /**
     * Why the grid takes no new segment now, in the spärr's own words, or null when it does. A stopped tool
     * draws no row, so a segment added then would come back as nothing and vanish
     * (stepdrill-preview.test.ts, "while the tool stops …").
     */
    private get _addRefusal(): string | null {
        const detail = this.item?.detail;
        if (!detail?.stoppedAt?.length) return null;
        if (this.item?.status === 'Error' && this.item.errorMessage) return this.item.errorMessage.split(' | ').join(' ');
        return `Väntar på ${(detail.waitingOn?.length ? detail.waitingOn : detail.stoppedAt).join(', ')}`;
    }

    private _add() {
        if (this._addRefusal !== null) return;
        // Inherit Ø from the previous step: a step drill's diameters are non-decreasing toward
        // the shank, so same-Ø is the safe lower bound — and it stops the silhouette pinching to
        // the centre line on a 0-Ø segment (the actual cause of the "weird SVG"). Length is NOT
        // inherited (no reliable relationship); it stays 0, which with the inherited Ø is a
        // geometric no-op, so the drawing stays clean until the user sets L.
        const prev = this._segments[this._segments.length - 1];
        const seed = blankSegment();
        if (prev) seed.diameter = prev.diameter;
        if (this._editing.size > 0) this._editing = new Map();
        this._dispatchNow([...this._segments, seed]);
    }

    private _remove(i: number) {
        if (this._editing.size > 0) this._editing = new Map();
        this._dispatchNow(this._segments.filter((_, idx) => idx !== i));
    }

    /** A DISCRETE act on the list — add, remove, a reset click — dispatches at once, not through the
     *  keystroke debounce: the drawing follows the click. Any pending debounced dispatch is dropped
     *  first, because it would carry a draft this one supersedes. */
    private _dispatchNow(next: SegmentInput[]) {
        if (this._dispatchTimer !== null) { clearTimeout(this._dispatchTimer); this._dispatchTimer = null; }
        this._draft = next;
        this._dispatch(next);
    }

    private _editField(i: number, key: keyof SegmentInput, value: unknown) {
        // The placeholder row becomes REAL here, on the first value — not when it was drawn. Without
        // this the edit would map over an empty list and vanish, which is the trap in showing a row
        // that does not exist yet.
        const base = this._displaySegments;
        const materialising = base.length !== this._segments.length;
        const next = base.map((s, idx) =>
            idx === i ? { ...s, [key]: value } : s,
        );
        this._draft = next;
        // Creating the placeholder row is STRUCTURAL, so it dispatches at once — the same rule
        // `_add`/`_remove` follow, and for the same reason. Debounced, the 200 ms window sits open
        // while `updated()` adopts any incoming server echo whenever the row COUNTS differ (1 vs 0),
        // which is exactly the shape of this edit. The customer's first keystroke would be wiped by
        // a refresh she did not cause.
        if (materialising) this._dispatch(next);
        else this._scheduleDispatch();
    }

    /** 200ms debounce — coalesces a flurry of keystrokes into a single POST.
     *  Cancelled on every new edit, so the timer only fires when the user
     *  pauses typing. Add/remove still dispatch immediately (structural). */
    private _scheduleDispatch() {
        if (this._dispatchTimer !== null) clearTimeout(this._dispatchTimer);
        this._dispatchTimer = setTimeout(() => {
            this._dispatchTimer = null;
            if (this._draft !== null) this._dispatch(this._draft);
        }, 200);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this._dispatchTimer !== null) {
            clearTimeout(this._dispatchTimer);
            this._dispatchTimer = null;
        }
        if (this._approachTimer !== null) {
            clearTimeout(this._approachTimer);
            this._approachTimer = null;
        }
        this._trackObserver?.disconnect();
        this._trackObserver = null;
        this._observedGrid = null;
    }

    /** Sum of one measure across the segment's operations. Takes any key, not just the reserved
     *  pair — an operation carries whatever measures its author gave it. */
    private _segmentTotal(i: number, key: string): number {
        const ops = this.item?.detail?.segments?.[i]?.operationResults;
        if (!ops) return 0;
        let sum = 0;
        for (const r of Object.values(ops)) {
            sum += measureValue(r, key);
        }
        return sum;
    }

    /** Cone length leading INTO segment i, computed server-side from (d[i] - d[i-1]) and α[i]. On
     *  row 0 that cone IS the tip (stegborr-segmentmodellen 19 — spetsen är segment 1:s kon), so
     *  the KONA cell of the first row shows the tip length; there is no tip row above it. */
    private _transitionLength(i: number): number {
        return Number(this.item?.detail?.segments?.[i]?.transitionLength ?? 0);
    }

    /** Axial position from the APEX to the END of segment i's cylinder — the server's `posFromTip`,
     *  read as sent. The client used to derive it (tipLength, then Σ transition + cylinder), which
     *  double-counted the tip the day row 0's transition BECAME the tip length: every readout sat
     *  0,93 mm too far on Mattias' tool. The server is the single source of the positions; the
     *  client reads, it does not re-derive. */
    private _cumFromTip(i: number): number {
        return Number(this.item?.detail?.segments?.[i]?.posFromTip ?? 0);
    }

    /** Position from the base of step 1 (the tip cone excluded) — the server's `posFromStep1`,
     *  which is how 93776 dimensions tolerated lengths from the step-1 base. */
    private _cumFromStep1(i: number): number {
        return Number(this.item?.detail?.segments?.[i]?.posFromStep1 ?? 0);
    }

    /** Does the POS cell carry an after-text at all? No when the cell's own value ALREADY IS the
     *  position from the tip — an echo of what you just typed is the "kaka på kaka" the old
     *  sub-row was criticised for, one cell closer. Read by the cell and by the column heading,
     *  so the heading can never promise a reading the cells don't show. */
    private _showsPosEcho(): boolean {
        return this.item?.detail?.measurementMode !== 'AbsoluteFromTip';
    }
    /**
     * The derived axial position, as a small grey after-text inside the POS cell (issue 11,
     * decision 2). It used to be a row of its own under every segment — which doubled the height
     * of the whole grid, the single largest chunk of it.
     *
     * ONE number, and always the same one: the position from the TIP, which is the reading the
     * drawing's own dimension chain is built on and the one a declared `pos` rule addresses. The
     * from-Step-1 reading no longer has a place of its own on screen; it rides in the title, and
     * that IS a loss — a title does not exist on a touch screen. It is stated as such in issue 11's
     * comments rather than hidden here. When the cell IS the from-tip position (AbsoluteFromTip)
     * there is no after-text at all, and a `pos` break then marks the input — see `_renderSegment`.
     *
     * The mark is PAINT ONLY (a wavy underline here, a dashed outline on the cell). A glyph
     * appended to the text widened the box and shoved the field beside it, which is the same jump
     * the status bar exists to remove — one cell down.
     *
     * The title is built for every row on every render, because a title attribute is eager. It is
     * two formats and a template literal over numbers that are already at hand since the positions
     * became a prefix sum; the expensive half — the walk — is what moved out.
     */
    private _renderPosEcho(i: number) {
        if (!this._showsPosEcho()) return nothing;
        // A rule may own `pos`, which has NO input cell — it is derived from the lengths above it.
        // The readout is the only place a person can see it, so that is where the mark goes; the
        // cell they must actually change is the length, and the message says so.
        const posIssue = this._posIssue(i);
        const posBound = this._cellBoundText(i, 'pos') ?? this._cellBoundText(i, 'position');
        const reading = `Position från spets ${formatSv(this._cumFromTip(i), 2)} mm`
            + ` · från Steg 1 ${formatSv(this._cumFromStep1(i), 2)} mm`;
        const title = posIssue
            ? `${reading}\n${posIssue.message}`
            : posBound ? `${reading}\n${posBound}` : reading;
        return html`<span
            class="pos-echo ${posIssue ? 'pos-broken' : ''}"
            data-mark="stepdrill-cell:pos-echo"
            title=${title}
        >⟨${formatSv(this._cumFromTip(i), 2)}⟩</span>`;
    }

    /** The break marking a row's derived POSITION, under either column name the author may have
     *  declared it with. One helper because three places ask — the after-text, the cell's outline,
     *  and the fallback onto the field in AbsoluteFromTip mode — and they must agree. */
    private _posIssue(i: number): { message: string; blocking: boolean } | undefined {
        return this._cellMarks.get(IxitStepdrillPreview._markKey(i, 'pos'))
            ?? this._cellMarks.get(IxitStepdrillPreview._markKey(i, 'position'));
    }

    /** Row+column → the break(s) marking that cell, and the bound on it. Rebuilt when the item
     *  changes rather than searched per cell: `?error=` and `title=` each asked, for four inputs
     *  plus the position readout, which made it a linear scan per cell per render at keystroke
     *  rate. The key's segments are LENGTH-PREFIXED because a column name may contain anything —
     *  a plain separator lets two different (row, column) pairs collide on one key. */
    private _cellMarks = new Map<string, { message: string; blocking: boolean }>();
    private _cellBounds = new Map<string, string>();

    private static _markKey(i: number, column: string): string {
        return `${i}:${column.length}:${column}`;
    }

    /** Rebuilds `_segmentTones` — but only when the drawing actually changed (see the field). */
    private _indexSegmentTones() {
        const svg = this.item?.silhouetteSvg;
        if (svg === this._lastTonesSvg) return;
        this._lastTonesSvg = svg;
        this._segmentTones = readSegmentTones(svg);
    }

    /**
     * The inline style that paints a row's segment tone, or nothing when the drawing has no band
     * for it (a thumbnail-only payload, a tool with no drawing yet). Row `#1` carries `.seg0`'s
     * tone, and that band covers the tip cone too — the tip IS segment 1's cone.
     */
    private _rowTone(idx: string): string | typeof nothing {
        const tone = this._segmentTones.get(this._segTag(idx));
        // The tone is translucent by construction (segment-tones.ts mixes the band's fill with
        // transparent), so it has to composite over a ground rather than be one. As a background
        // IMAGE it does exactly that, and leaves background-color free for the opaque ground the
        // frozen index column needs (stegborr-segmentmodellen 28).
        return tone ? `background-image:linear-gradient(${tone},${tone})` : nothing;
    }

    /** The rule break marking one cell, if any. Row + column addressed, so a rule broken by an
     *  edit two rows up lands on the row that now violates it — not on the one that changed.
     *  Several rules may own one cell; all of their messages are shown, not just the first. */
    private _cellIssue(i: number, column: string): string | undefined {
        return this._cellMarks.get(IxitStepdrillPreview._markKey(i, column))?.message;
    }

    /** Whether the break on this cell REFUSES the hand-off or merely says something. The cell has
     *  to be able to tell the two apart on its own — the acceptance line asks for the distinction
     *  "i statusraden såväl som på cellen", and until the severity was carried here every break
     *  painted the same danger dash. Returns undefined when nothing marks the cell. */
    private _cellSeverity(i: number, ...columns: string[]): 'blocking' | 'advisory' | undefined {
        for (const column of columns) {
            const mark = this._cellMarks.get(IxitStepdrillPreview._markKey(i, column));
            if (mark) return mark.blocking ? 'blocking' : 'advisory';
        }
        return undefined;
    }

    /** The live bound a declared rule puts on this cell, as a short label — or undefined when no
     *  rule owns the column. */
    private _cellBoundText(i: number, column: string): string | undefined {
        return this._cellBounds.get(IxitStepdrillPreview._markKey(i, column));
    }

    /** Indexes the server's row-addressed marks and bounds once per payload. */
    private _indexCellMarks() {
        const marks = new Map<string, { message: string; blocking: boolean }>();
        for (const issue of this.item?.detail?.segmentIssues ?? []) {
            const key = IxitStepdrillPreview._markKey(issue.segmentIndex, issue.column);
            const seen = marks.get(key);
            // Severity travels with the message. Several rules may own one cell; the STRICTEST wins
            // the cell's own mark, because a cell that refuses the hand-off must not be drawn as an
            // advisory just because a softer rule was indexed after it.
            marks.set(key, {
                message: seen ? `${seen.message}\n${issue.message}` : issue.message,
                blocking: (seen?.blocking ?? false) || issue.severity === 'Blocking',
            });
        }
        this._cellMarks = marks;

        const bounds = new Map<string, string>();
        for (const bound of this.item?.detail?.segmentBounds ?? []) {
            const min = typeof bound.min === 'number' ? compactSv(bound.min) : undefined;
            const max = typeof bound.max === 'number' ? compactSv(bound.max) : undefined;
            const text = min !== undefined && max !== undefined ? `${min}–${max}`
                : min !== undefined ? `min ${min}`
                : max !== undefined ? `max ${max}`
                : undefined;
            if (text !== undefined) bounds.set(IxitStepdrillPreview._markKey(bound.segmentIndex, bound.column), text);
        }
        this._cellBounds = bounds;
    }

    /** The cell's PLACEHOLDER — which is where a declared bound belongs.
     *
     *  It used to render into uui-input's `append` slot, and that was wrong twice over: "10,00–20,00"
     *  measured 66px inside an 80px cell and spilled 47px into the next column, and a suffix competes
     *  for width with the very number it is meant to guide. The placeholder costs NOTHING — it
     *  occupies the empty field, and the browser removes it the moment a character is typed.
     *
     *  It only works because an absent Ø renders as an empty string (see `_cellValue`), which is
     *  true for `diameter` alone. Other columns fall back to their ordinary placeholder and carry
     *  their bound in the title. */
    private _cellPlaceholder(i: number, column: string, fallback: string): string {
        if (!ABSENT_AS_EMPTY.has(column)) return fallback;
        return this._cellBoundText(i, column) ?? fallback;
    }

    /** Δ-cell content. In Relative mode just shows the cone length. In absolute
     *  modes (AbsoluteFromTip/AbsoluteFromStep1) also shows the canonical cyl
     *  length the user effectively chose by entering a position — gives them
     *  immediate insight into the kona+cyl split. */
    private _renderDeltaCell(i: number) {
        const trans = formatSv(this._transitionLength(i), 2);
        const mode = this.item?.detail?.measurementMode;
        if (mode === 'Relative' || !mode) {
            return html`${trans}`;
        }
        const cylRaw = Number(this.item?.detail?.segments?.[i]?.cylinderLength ?? 0);
        return html`${trans} <span class="cyl-split">/ cyl ${formatSv(cylRaw, 2)}</span>`;
    }

    /** Operation names in the order they appear on the first segment's result.
     *  Empty array when no ops are configured (or no segments yet).
     *  Used for the "Operationer: …" header banner + per-op breakdown labels. */
    private get _operationNames(): string[] {
        const first = this.item?.detail?.segments?.[0]?.operationResults;
        if (!first) return [];
        return Object.keys(first);
    }

    private _onNumberInput(i: number, key: keyof SegmentInput) {
        return (e: Event) => {
            const raw = (e.target as HTMLInputElement).value;
            const cellKey = `${i}:${String(key)}`;
            // Stash raw string so re-render of this cell uses verbatim user input
            // (instead of re-formatting and clobbering trailing decimals)
            const next = new Map(this._editing);
            next.set(cellKey, raw);
            this._editing = next;
            // Update the draft with the parsed number: it is what the CELLS render from until the
            // debounced dispatch round-trips. The derived readouts beside them — the Δ-cell, the
            // result and the position after-text — come from the server's echo instead, so they
            // lag by one round-trip on purpose rather than being recomputed here twice.
            this._editField(i, key, parseDecimal(raw));
        };
    }

    /** The standalone input the shank-approach override travels in — `{alias}._approachAngle`,
     *  read by the server under `StepdrillEvaluator.ApproachAngleInputSuffix`. */
    private get _approachInputAlias(): string {
        return `${this.item?.alias ?? ''}._approachAngle`;
    }

    /**
     * The PERSISTED shank-approach override, or null when the shank inherits the admin default.
     * Read off the host's live input dictionary (`state.inputValues`), which is the only place the
     * override exists: the server echoes the RESOLVED angle (`detail.approachAngle`) and that number
     * is the same whether it came from the formula or from a typed value — so the detail alone
     * cannot say which, and a cell that showed it as a value made every shank look overridden.
     */
    private _approachOverride(): number | null {
        const raw = this.state?.inputValues?.[this._approachInputAlias];
        const n = parseDecimal(raw);
        return n > 0 ? n : null;
    }

    /** Sends the override — a positive angle, or null for "inherit". Never the default itself. */
    private _sendApproachOverride(value: number | null) {
        const stepAlias = this.state?.stepAlias ?? this.state?.name ?? '';
        if (this.item) this.onInputChange(stepAlias, this._approachInputAlias, value);
    }

    /** Tier-3 end-user override of the shank-approach angle. Mirrors the segment number-input
     *  draft (raw stash in _editing under a sentinel key) but dispatches the STANDALONE
     *  `{alias}._approachAngle` input — independent of the segments, so it doesn't ride
     *  _dispatch's segments+mode payload. The backend resolves three tiers (admin formula
     *  default → this override → clamp to [min,max]) and echoes the effective value back. */
    private _onApproachAngleInput = (e: Event) => {
        const raw = (e.target as HTMLInputElement).value;
        const next = new Map(this._editing);
        next.set('shank:approachAngle', raw);
        this._editing = next;
        // Empty/cleared → null = INHERIT: the cell goes back to showing the admin default as its
        // placeholder, and the input is cleared rather than set to a misleading 0.
        const typed = raw.trim() === '' ? null : parseDecimal(raw);
        const value = typed !== null && typed > 0 ? typed : null;
        this._approachDraft = value;
        if (this._approachTimer !== null) clearTimeout(this._approachTimer);
        // The keystroke's OWN value rides in the closure — never re-read off `_approachDraft` when
        // the timer fires. Read that way, a draft cleared by an echo in the meantime (`undefined`)
        // was sent as `?? null`, i.e. as a RESET: the user typed 30 and the host was told "inherit".
        this._approachTimer = window.setTimeout(() => {
            this._approachTimer = null;
            this._sendApproachOverride(value);
        }, 200);
    };

    private _onApproachAngleBlur = () => {
        if (!this._editing.has('shank:approachAngle')) return;
        const next = new Map(this._editing);
        next.delete('shank:approachAngle');
        this._editing = next;
    };

    /** The ↺ on row #1: back to inheriting the PointAngle formula, at once — `_dispatchNow`, like
     *  add/remove, so the drawing follows the click rather than a debounce. Writes 0 into the
     *  segment, which is the wire's "inherit"; never the resolved angle. */
    private _onPointAngleReset = () => {
        const cellKey = `${POINT_ANGLE_ROW}:incomingAngle`;
        if (this._editing.has(cellKey)) {
            const next = new Map(this._editing);
            next.delete(cellKey);
            this._editing = next;
        }
        this._dispatchNow(this._displaySegments.map((s, idx) =>
            idx === POINT_ANGLE_ROW ? { ...s, incomingAngle: 0 } : s));
    };

    /** The ↺ on the shank row: back to inheriting, at once (a click is not a keystroke). */
    private _onApproachAngleReset = () => {
        if (this._approachTimer !== null) { clearTimeout(this._approachTimer); this._approachTimer = null; }
        this._onApproachAngleBlur();
        this._approachDraft = null;
        this._sendApproachOverride(null);
        // The draft is not reactive state (it is a cache of the last keystroke), so the cell is
        // asked to redraw — it must read as inheriting NOW, not when the host's echo arrives.
        this.requestUpdate();
    };

    /** End-user override of measurement mode. Backend reads `{alias}._mode`
     *  from ctx.Inputs and echoes it back as detail.measurementMode. The dot
     *  suffix is deliberate — formulas can't reference dotted identifiers, so
     *  it can't collide with author-defined field aliases. */
    private _onModeChange = (e: Event) => {
        const mode = (e.target as HTMLSelectElement).value as StepdrillMeasurementMode;
        // Re-express each segment's `length` in the NEW mode from the server's mode-INDEPENDENT
        // geometry (cylinderLength / posFromTip / posFromStep1). These arrive as JSON numbers →
        // JS doubles; the back-calculated cylinder length is a non-round geometric value (it
        // subtracts a Math.Tan-derived cone), so adopting it verbatim, re-sending, and re-summing
        // on the server injects binary-float noise (28.67 → 28.67000000000006) that COMPOUNDS every
        // time the mode is flipped. Snap to 6 decimals (1 nm) on adoption: kills the ~1e-13 noise,
        // keeps geometric precision far below any drill tolerance, and the cell shows 2 dp anyway.
        const snap = (x: number) => Math.round(x * 1e6) / 1e6;
        const serverSegs = this.item?.detail?.segments;
        let next = this._segments;
        // Only computed rows carry the geometry to re-express from: under a stop the server echoes the
        // customer's rows with none, and a stop takes the computation away, never her input. Asked of
        // segmentCount, not stoppedAt, because production mode withholds stoppedAt
        // ("a change of Mätreferens under a stop keeps the customer's lengths…").
        if (serverSegs && serverSegs.length === next.length && this.item?.detail?.segmentCount === next.length) {
            next = next.map((s, i) => {
                const r = serverSegs[i];
                // A row with NO CYLINDER keeps its 0 in every mode. In the absolute modes 0 does
                // not mean "position 0" — the backend reads it as "this row has no cylinder", the
                // reading a purely conical step carries, whose whole axial extent is the engine's
                // ConeLength(Δr, α). Re-expressing it as its END position would write a DERIVED
                // number into the cell (40,4 on 93776) and make the row's zero depend on the angle:
                // touch α afterwards and the cone moves while the position does not, so the row
                // grows a phantom cylinder — or goes negative and is clamped with a warning.
                // Relative is unaffected either way, since cylinderLength is 0 there too.
                const newLength = r.cylinderLength === 0 ? 0 : snap(
                    mode === 'AbsoluteFromTip' ? r.posFromTip
                    : mode === 'AbsoluteFromStep1' ? r.posFromStep1
                    : r.cylinderLength);
                return { ...s, length: newLength };
            });
            this._draft = next;
            if (this._editing.size > 0) this._editing = new Map(); // in-progress raw edits are stale across modes
        }
        this._dispatch(next, mode);
    };

    /** Column label for the segment length depends on what the value means
     *  in the active measurement mode. Kept short so the grid column doesn't
     *  resize when end-user toggles modes — (mm)-unit moves to the title
     *  tooltip. Power-user sets the default in backoffice; end-user can
     *  override via the header dropdown. */
    private _lengthHeaderLabel(): { text: string; title: string } {
        switch (this.item?.detail?.measurementMode) {
            case 'AbsoluteFromTip':
                return { text: 'Pos→spets', title: 'Position från drill-spets (mm)' };
            case 'AbsoluteFromStep1':
                return { text: 'Pos→Steg 1', title: 'Position från Steg #1-bas (mm)' };
            case 'Relative':
            default:
                return { text: 'L cyl', title: 'Cylindrisk längd (mm)' };
        }
    }

    /**
     * A row the block's formulas wrote: the same cells as a typed row, in the same order, as text.
     * It must emit the same number of cells as every other row, or the shared template slides
     * (stepdrill-preview.test.ts, "the formula row occupies the same number of columns").
     */
    private _renderFormulaSegment(seg: SegmentInput, i: number) {
        const lengthLabel = this._lengthHeaderLabel();
        const tol = this.item?.detail?.segments?.[i]?.tolerance ?? undefined;
        const tolText = typeof tol?.value === 'number' ? `±${compactSv(tol.value)}` : '—';
        const text = (key: string, label: string, value: string, title?: string) => html`
            <label class="cell readonly" data-key=${key} data-severity=${this._cellSeverity(i, key) ?? nothing}>
                <span class="cell-label">${label}</span>
                <span class="readonly-cell" title=${title ?? nothing}>${value}</span>
            </label>`;
        // A cell the server left out got no value from its formula; `_serverSegments` reads it as 0, and
        // a zero is a value the formula never gave (stepdrill-preview.test.ts, "a formula row whose length
        // the server left out shows").
        const lengthGiven = this.item?.detail?.segments?.[i]?.input?.['length'] != null;
        // Row 1 without a Vinkel of its own is the point angle, shown as inherited, as the customer's empty
        // α is, and said in words under the value (stepdrill-preview.test.ts, "the formula row's angle"). A
        // row only reaches here without one when the cell is empty: a failed Vinkel builds no row.
        const angleGiven = this.item?.detail?.segments?.[i]?.input?.['incomingAngle'] != null;
        const inherits = i === POINT_ANGLE_ROW && !angleGiven;
        const angle = i === POINT_ANGLE_ROW
            ? `${compactSv(Number(this.item?.detail?.tipAngle ?? 0))}°`
            : `${compactSv(Number(seg.incomingAngle ?? 0))}°`;
        return html`
            <div class="segment-card" data-segment-index=${i}>
            <div
                class="row body from-formula ${this._rowVariant}"
                data-segment-index=${i}
                title="Ur formel — bestäms av kalkylatorn"
            >
                <span class="idx" data-mark="stepdrill-seg:tone" style=${this._rowTone(String(i))}>#${i + 1}<span
                    class="source-mark" data-mark="stepdrill-seg:source">ur formel</span></span>
                <label
                    class="cell readonly"
                    data-key="incomingAngle"
                    data-angle-source=${inherits ? 'inherited' : 'formula'}
                    data-severity=${this._cellSeverity(i, 'incomingAngle') ?? nothing}
                >
                    <span class="cell-label">${i === POINT_ANGLE_ROW ? 'α spets' : 'α'}</span>
                    <span
                        class="readonly-cell ${inherits ? 'inherited with-source' : ''}"
                        title=${inherits ? `Ärvt ${angle}: formelradens Vinkel är tom, så segmentet har spetsvinkeln.` : 'Ur formelradens Vinkel.'}
                    >${angle}${inherits
                        ? html` <span class="angle-source" data-mark="stepdrill-seg:angle-source">spetsvinkeln</span>`
                        : nothing}</span>
                </label>
                ${text('diameter', 'Ø', seg.diameter > 0 ? formatSv(seg.diameter, 2) : '—')}
                ${text('tolerance', 'Tol.', tolText)}
                <label
                    class="cell pos-cell readonly"
                    data-key="length"
                    data-severity=${this._cellSeverity(i, 'length') ?? nothing}
                >
                    <span class="cell-label">${lengthLabel.text}</span>
                    <span class="readonly-cell">${lengthGiven ? formatSv(seg.length, 2) : '—'}</span>
                    ${this._renderPosEcho(i)}
                </label>
                ${this._showsTransitionColumns ? html`
                <label class="cell readonly" data-key="kona">
                    <span class="cell-label">Kona</span>
                    <span class="readonly-cell">${this._renderDeltaCell(i)}</span>
                </label>
                ${text('incomingCornerRadius', 'r', formatSv(seg.incomingCornerRadius, 2))}
                ` : nothing}
                ${this._renderOpsCells((op, key) => formatSv(measureValue(
                    this.item?.detail?.segments?.[i]?.operationResults?.[op], key), 1))}
                ${this._renderSegmentResult(i)}
                <span></span>
            </div>
            </div>
        `;
    }

    /**
     * Whether the block's formula row waits: the server builds no row then, so the tool has no number of
     * its own until the formula has its value. Status rather than `waitingOn`, which production withholds.
     */
    private get _formulaRowWaits(): boolean {
        return this._segmentsFromFormulas
            && this.item?.status === 'AwaitingInput'
            && (this.item?.detail?.segments?.length ?? 0) === 0;
    }

    /** Whether a formula failed: the server builds no row then either, and the error names the formula. */
    private get _formulaRowFails(): boolean {
        return this._segmentsFromFormulas
            && this.item?.status === 'Error'
            && (this.item?.detail?.segments?.length ?? 0) === 0;
    }

    /** The formula row the server did not build, because a formula waits or fails: nothing was computed. */
    private get _formulaRowUnbuilt(): boolean {
        return this._formulaRowWaits || this._formulaRowFails;
    }

    /**
     * The formula row the server did not build: marked "väntar" while a formula or Formlerna gäller när waits,
     * because the detail does not say which of them waits, and "formelfel" when one fails; never "ur
     * formel". "—" in every cell, the same cells as every other row so the shared template holds
     * (stepdrill-preview.test.ts, "a waiting formula row is drawn", "a formula row whose formula fails").
     */
    private _renderWaitingFormulaRow() {
        const fails = this._formulaRowFails;
        const dash = (key: string, label: string) => html`
            <label class="cell readonly" data-key=${key}>
                <span class="cell-label">${label}</span>
                <span class="readonly-cell">—</span>
            </label>`;
        return html`
            <div class="segment-card" data-segment-index="0">
            <div
                class="row body from-formula ${this._rowVariant}"
                data-segment-index="0"
                title=${fails
                    ? 'Formelfel — raden räknas när formeln är rättad'
                    : 'Väntar — raden räknas när det den väntar på har ett värde'}
            >
                <span class="idx" data-mark="stepdrill-seg:tone" style=${this._rowTone('0')}>#1<span
                    class="source-mark" data-mark="stepdrill-seg:source">${fails ? 'formelfel' : 'väntar'}</span></span>
                ${dash('incomingAngle', 'α spets')}
                ${dash('diameter', 'Ø')}
                ${dash('tolerance', 'Tol.')}
                <label class="cell pos-cell readonly" data-key="length">
                    <span class="cell-label">${this._lengthHeaderLabel().text}</span>
                    <span class="readonly-cell">—</span>
                </label>
                ${this._showsTransitionColumns
                    ? html`${dash('kona', 'Kona')}${dash('incomingCornerRadius', 'r')}`
                    : nothing}
                ${this._renderOpsCells(() => '—')}
                <div class="result"><div class="result-total">—</div></div>
                <span></span>
            </div>
            </div>
        `;
    }

    private _renderSegment(seg: SegmentInput, i: number) {
        const lengthLabel = this._lengthHeaderLabel();
        // A `pos` rule has no cell of its own; it normally marks the after-text and the cell around
        // it. In AbsoluteFromTip mode there IS no after-text — the field itself holds the position —
        // so the break folds onto the field, which is also the thing the reader would have to change.
        const posIssue = this._posIssue(i);
        const foldsOntoField = !this._showsPosEcho();
        const lengthIssue = this._cellIssue(i, 'length') ?? (foldsOntoField ? posIssue?.message : undefined);
        const lengthSeverity = this._cellSeverity(i, 'length')
            ?? (foldsOntoField ? this._cellSeverity(i, 'pos', 'position') : undefined);
        const posCellSeverity = foldsOntoField ? undefined : this._cellSeverity(i, 'pos', 'position');
        // Wrap the segment's row in a .segment-card. On desktop it's display: contents so the grid
        // flow stays unchanged. In card mode it becomes a flex column so the auto-fit grid places
        // each segment as one card.
        return html`
            <div class="segment-card" data-segment-index=${i}>
            <div class="row body ${this._rowVariant}" data-segment-index=${i}>
                <span class="idx" data-mark="stepdrill-seg:tone" style=${this._rowTone(String(i))}>#${i + 1}</span>
                ${i === POINT_ANGLE_ROW
                    // Row 1's cone is the TIP, and its α is the included point angle with the
                    // PointAngle formula behind it — the inherit-or-override cell, shared with the
                    // shank row. Every other row's α is a plain transition angle from the axis.
                    ? this._renderPointAngleCell(seg)
                    : html`
                <label
                    class="cell"
                    data-key="incomingAngle"
                    data-severity=${this._cellSeverity(i, 'incomingAngle') ?? nothing}
                >
                    <span class="cell-label">α</span>
                    <uui-input
                        type="text"
                        inputmode="decimal"
                        placeholder="α"
                        label="Segment ${i + 1} kon-vinkel α (grader från axeln)"
                        ?error=${this._cellIssue(i, 'incomingAngle') !== undefined}
                        title=${this._cellIssue(i, 'incomingAngle') ?? this._cellBoundText(i, 'incomingAngle') ?? nothing}
                        .value=${this._cellValue(i, 'incomingAngle', seg.incomingAngle)}
                        @input=${this._onNumberInput(i, 'incomingAngle')}
                        @blur=${this._onNumberBlur(i, 'incomingAngle')}
                    ></uui-input>
                </label>`}
                <label
                    class="cell"
                    data-key="diameter"
                    data-severity=${this._cellSeverity(i, 'diameter') ?? nothing}
                >
                    <span class="cell-label">Ø</span>
                    <uui-input
                        type="text"
                        inputmode="decimal"
                        placeholder=${this._cellPlaceholder(i, 'diameter', 'Ø')}
                        label="Segment ${i + 1} diameter (mm)"
                        ?error=${this._cellIssue(i, 'diameter') !== undefined}
                        title=${this._cellIssue(i, 'diameter') ?? this._cellBoundText(i, 'diameter') ?? nothing}
                        .value=${this._cellValue(i, 'diameter', seg.diameter)}
                        @input=${this._onNumberInput(i, 'diameter')}
                        @blur=${this._onNumberBlur(i, 'diameter')}
                    ></uui-input>
                </label>
                ${this._renderToleranceCell(seg, i)}
                <label
                    class="cell pos-cell"
                    data-key="length"
                    data-severity=${lengthSeverity ?? nothing}
                    data-pos-severity=${posCellSeverity ?? nothing}
                >
                    <span class="cell-label">${lengthLabel.text}</span>
                    <uui-input
                        type="text"
                        inputmode="decimal"
                        placeholder="L"
                        label="Segment ${i + 1} ${lengthLabel.title.toLowerCase()}"
                        ?error=${lengthIssue !== undefined}
                        title=${lengthIssue ?? this._cellBoundText(i, 'length') ?? nothing}
                        .value=${this._cellValue(i, 'length', seg.length)}
                        @input=${this._onNumberInput(i, 'length')}
                        @blur=${this._onNumberBlur(i, 'length')}
                    ></uui-input>
                    ${this._renderPosEcho(i)}
                </label>
                ${this._showsTransitionColumns ? html`
                <label class="cell readonly" data-key="kona">
                    <span class="cell-label">Kona</span>
                    <span class="readonly-cell">${this._renderDeltaCell(i)}</span>
                </label>
                <label
                    class="cell"
                    data-key="incomingCornerRadius"
                    data-severity=${this._cellSeverity(i, 'incomingCornerRadius') ?? nothing}
                >
                    <span class="cell-label">r</span>
                    <uui-input
                        type="text"
                        inputmode="decimal"
                        placeholder="r"
                        label="Segment ${i + 1} innerradie r (mm)"
                        ?error=${this._cellIssue(i, 'incomingCornerRadius') !== undefined}
                        title=${this._cellIssue(i, 'incomingCornerRadius') ?? this._cellBoundText(i, 'incomingCornerRadius') ?? nothing}
                        .value=${this._cellValue(i, 'incomingCornerRadius', seg.incomingCornerRadius)}
                        @input=${this._onNumberInput(i, 'incomingCornerRadius')}
                        @blur=${this._onNumberBlur(i, 'incomingCornerRadius')}
                    ></uui-input>
                </label>
                ` : nothing}
                ${this._renderOpsCells((op, key) => formatSv(measureValue(
                    this.item?.detail?.segments?.[i]?.operationResults?.[op], key), 1))}
                ${this._renderSegmentResult(i)}
                ${canRemoveSegment(this._segments.length, this._maxSegments)
                    ? html`<button class="remove" title="Ta bort segment" @click=${() => this._remove(i)}>×</button>`
                    // An empty SPAN, not `nothing`: this is a grid CELL, and the head/tip/shank rows
                    // all emit one here. Dropping it left the segment row a cell short of the
                    // template, so every column after it slid left — the whole table came apart, and
                    // only in the browser. A hidden control still occupies its column.
                    : html`<span></span>`}
            </div>
            </div>
        `;
    }

    /**
     * The operations as a COLUMN AXIS: each operation with the measure keys it actually carries,
     * reserved pair first and then whatever its author added, in the order the server sent them.
     *
     * <p>Measures are unioned across segments rather than read off the first one: an operation whose
     * formula returns nothing on segment 1 still has its column, and a measure that only appears
     * further down the tool does not silently lose its heading. The operation ORDER comes from
     * {@link _operationNames} — the server writes them in the order the author declared them, which
     * is the order a cascade runs in.</p>
     *
     * <p><b>Indexed once per payload, not derived per reading.</b> It is read by the header, by every
     * segment row, by the tip and shank rows and by the totals row — segments + 6 times per render —
     * and each derivation walks every segment. As a getter that made the work
     * segments² × operations × measures at keystroke rate, which is the same shape this file already
     * fixed once and wrote down beside {@link _cumFromTip}: "a seven-step drill do 84 additions per
     * render at keystroke rate for seven numbers". Caught by the performance review, and the fix is
     * the pattern the file already had.</p>
     *
     * <p>The vocabulary comes from the shared helpers (`extraMeasureKeysOf`, `measureLabel`) and not
     * from a list written here — the browser and the server must agree about which keys are
     * reserved, and an inline copy is how they stop agreeing.</p>
     */
    private _outcomeColumns: { op: string; keys: string[] }[] = [];

    /** Rebuilds {@link _outcomeColumns}. Called from willUpdate, so once per payload. */
    private _indexOutcomeColumns() {
        const segments = this.item?.detail?.segments ?? [];
        const perOp = new Map<string, { keys: string[]; seen: Set<string> }>();

        for (const segment of segments) {
            for (const [op, measures] of Object.entries(segment.operationResults ?? {})) {
                let entry = perOp.get(op);
                if (!entry) perOp.set(op, entry = { keys: [], seen: new Set<string>() });
                for (const key of extraMeasureKeysOf(measures)) {
                    // A Set, not a linear scan: the scan was O(keys) inside a loop over every
                    // segment's every operation.
                    const fold = key.toLowerCase();
                    if (entry.seen.has(fold)) continue;
                    entry.seen.add(fold);
                    entry.keys.push(key);
                }
            }
        }

        this._outcomeColumns = this._operationNames.map((op) => ({
            op,
            keys: [SETUP_MEASURE, MACHINE_MEASURE, ...(perOp.get(op)?.keys ?? [])],
        }));

        // The column sums, in the same pass. The totals row asked for them per CELL per render,
        // which walks every segment again for every column — same axis, same mistake, one order
        // smaller. Keyed by operation and measure, both compared as stored.
        // Nested rather than one map under a joined key: an operation name is an AUTHORED string
        // and any separator you pick is a character it may legally contain.
        this._outcomeSums = new Map<string, Map<string, number>>();
        for (const column of this._outcomeColumns) {
            const sums = new Map<string, number>();
            for (const key of column.keys) {
                let sum = 0;
                for (const segment of segments) {
                    sum += measureValue(segment.operationResults?.[column.op], key);
                }
                sums.set(key, sum);
            }
            this._outcomeSums.set(column.op, sums);
        }
    }

    /** Column sums for the totals row, indexed beside the columns themselves — operation, then
     *  measure. See {@link _indexOutcomeColumns} for why the totals row does not sum per render. */
    private _outcomeSums = new Map<string, Map<string, number>>();

    /** Result cell: top line shows segment totals, below shows the per-operation breakdown so
     *  authors can see which op contributed what. The breakdown used to collapse when a tool had a
     *  SINGLE operation, on the ground that it "would just echo the totals" — true while an
     *  operation was a fixed setup/machine pair, and false since it could carry a third measure:
     *  a lone operation's custom measure then appeared nowhere at all. It always renders now, and
     *  the grid below carries the same numbers with an axis. */
    private _renderSegmentResult(i: number) {
        const setupTotal = this._segmentTotal(i, SETUP_MEASURE);
        const machineTotal = this._segmentTotal(i, MACHINE_MEASURE);

        // The ROW TOTAL, and only that. It used to carry a per-operation breakdown underneath —
        // "rund: 2+4,8 · verktygsByte 3" — which was the only place those numbers existed while the
        // measures had no columns of their own. They have columns now, three of them to the left of
        // this cell, so the breakdown was the same reading printed twice: Andreas saw it as
        // "dubblerad information" the first time he looked at the merged table.
        //
        // The two TIMES are what a segment total means. A measure beyond the reserved pair is not
        // added in here — which total a measure feeds is a question about its ROLE, answered in
        //04 — roll, enhet, aggregat, and the tool-level answer is in the Σ row at the bottom.
        return html`
            <div class="result">
                <div class="result-total">
                    setup <strong>${formatSv(setupTotal, 1)}</strong>
                    · maskin <strong>${formatSv(machineTotal, 1)}</strong>
                </div>
            </div>
        `;
    }

    /**
     * The part strip: WHY the part count is what it is, next to the rows it was derived from
     * (issue 11). Renders `Delar: D · f · D · f · D · f (6)`, each chip pointing at the row it was
     * drawn on, so the author never has to take the number on trust — and never types it.
     *
     * <p><b>It never spells a benämning, and that is deliberate.</b> Until 2026-08-24 the strip
     * wrote `p+{n}` — a hardcoded literal. The prefix belongs to the GEOMETRY the nomenclature
     * block resolves to (`p+`, `a+`, `cb-`, …), and this plugin has no geometry awareness at all:
     * nothing about nomenclature reaches its detail payload, and the nomenclature node is
     * sequenced AFTER this item, so the prefix does not exist when the payload is built. A stepdrill
     * under an `a+` geometry therefore read `p+6`, and a single-diameter tool would read `p+1` — a
     * name that is never written, since the templates print no digit below two. The strip answers
     * "what was counted"; the name is shown where it is actually resolved. See
     * Documentation/internal/stegborr-segmentmodellen/issues/13-*.md, decision B3.</p>
     *
     * Hovering a chip drives the same silhouette highlight as focusing the row's field, reusing
     * _activeSeg/_activeMeasure (diameter chips accent the Ø, transition/chamfer chips the angle).
     *
     * Deliberately NOT in the nomenclature block: that view is tool-agnostic Core surface and must
     * not learn what a drilling diameter is, and the part→row link only means anything beside the
     * rows. See Documentation/internal/nomenclature-dispatch-and-consumption/issues/11-*.md.
     */
    private _renderPartStrip() {
        const d = this.item?.detail;
        const parts = d?.nameParts ?? [];
        if (!parts.length) return nothing;

        const rowName = (i: number) => (i === SHANK_CHAMFER_ROW ? 'härledd skaftfas' : `rad ${i + 1}`);
        const glyph = (p: NamePart) => (p.kind === 'diameter' ? 'D' : 'f');
        // Derived ONCE per part, not once per helper call: `describe` runs for every part on every
        // render (the title attribute is eager, even though only a hovered part is ever read), and
        // hovering sets @state — so the strip re-renders on every hover.
        const describe = (p: NamePart, cones: number[]) => {
            if (p.kind === 'diameter') {
                return `Borrande Ø ${formatSv(p.value, 2)} mm — ${rowName(p.segmentIndexes[0] ?? 0)}`;
            }
            const listed = cones
                .map((a, i) => `${formatSv(a, 1)}° (${rowName(p.segmentIndexes[i] ?? SHANK_CHAMFER_ROW)})`)
                .join(' + ');
            // A run of cones is ONE part — say so, because the strip shows a single f for it and
            // the reader is otherwise left counting cones that aren't there (issue 21).
            const what = cones.length > 1
                ? `Konlöpa utan cylinder emellan — räknas som EN del: ${listed}`
                : `${p.isShankChamfer ? 'Skaftfas' : 'Övergång'} ${listed}`;
            const radie = p.cornerRadius > 0
                ? ` · hörnradie ${formatSv(p.cornerRadius, 2)} mm — räknas som fas (obekräftat)`
                : '';
            return `${what}${radie}`;
        };
        // Every row a part is made of, so hovering ONE chip lights the whole run in the silhouette.
        const segTags = (p: NamePart) => p.segmentIndexes
            .map((i) => this._segTag(i === SHANK_CHAMFER_ROW ? SHANK_INDEX : String(i)))
            .join(' ');

        // What the DRILL DEPTH cut away (issue 23). Shown after the counted parts, inside angle
        // brackets and dimmed — two carriers, because the strip must stay readable without colour.
        // A count that silently got shorter is a number the author cannot check; this is where the
        // derivation is read, so it has to show the removal too.
        const cut = d?.partsOutsideDrillDepth ?? [];
        const cutTitle = cut.length === 1
            ? 'Ligger utanför borrdjupet och räknas inte'
            : `${cut.length} delar ligger utanför borrdjupet och räknas inte`;

        return html`
            <div class="part-strip">
                <span class="ps-label">Delar:</span>
                <span class="ps-parts">
                    ${parts.map((p, i) => {
                        const cones = p.kind === 'cone' ? (p.angles?.length ? p.angles : [p.value]) : [];
                        return html`
                        ${i > 0 ? html`<span class="ps-dot">·</span>` : nothing}
                        <span
                            class="ps-part ${p.kind === 'diameter' ? 'is-d' : 'is-f'} ${p.cornerRadius > 0 ? 'has-radius' : ''}"
                            title=${describe(p, cones)}
                            @mouseenter=${() => {
                                this._activeSeg = segTags(p);
                                this._activeMeasure = KEY_TO_MEASURE[PART_KIND_TO_KEY[p.kind]] ?? '';
                            }}
                            @mouseleave=${() => { this._activeSeg = ''; this._activeMeasure = ''; }}
                        >${glyph(p)}${cones.length > 1
                            ? html`<sup class="ps-run">${cones.length}</sup>`
                            : nothing}${p.cornerRadius > 0 ? html`<sup class="ps-radius">r</sup>` : nothing}</span>
                    `;
                    })}
                </span>
                ${cut.length ? html`
                    <span class="ps-cut" title=${cutTitle}
                    >⟨${cut.map((p, i) => html`${i > 0
                        ? html`<span class="ps-dot">·</span>`
                        : nothing}<span class="ps-part is-cut ${p.kind === 'diameter' ? 'is-d' : 'is-f'}"
                        >${glyph(p)}</span>`)}⟩</span>
                ` : nothing}
                <span class="ps-count">(${d?.partCount ?? parts.length})</span>
            </div>
        `;
    }

    /**
     * The status bar — where every verdict about this drill lives (issue 11, decision 1).
     *
     * <p>It is rendered whatever the state, including "nothing to say", and it is the LAST thing
     * in the block. Those two facts together are the whole point: its height is reserved, so a
     * break appearing or clearing cannot move anything on screen, and expanding it grows into
     * empty space rather than pushing the grid down. The bands used to sit ABOVE the grid, which
     * is exactly what "puttar ner hela GUI:t" described.</p>
     *
     * <p>Collapsed it carries the COUNT per severity, with the same three carriers the bands use —
     * glyph, the shape of the left rule, and a bold word — so spärr and upplysning stay tellable
     * apart without colour. Expanded, the sentences are unchanged.</p>
     */
    private _renderStatusBar(notices: StatusNotice[]) {
        const blocking = notices.filter((n) => n.blocking).length;
        const advisory = notices.length - blocking;
        const clean = notices.length === 0;
        return html`
            <div class="statusbar" data-mark="stepdrill-status">
                <div class="sb-line" role="status">
                    ${blocking > 0 ? html`
                        <span class="sb-count blocking" data-mark="stepdrill-status:blocking">
                            <span aria-hidden="true">⛔</span>
                            <span class="sb-n">${blocking}</span>
                            <span class="sb-kind">${blocking === 1 ? 'spärr' : 'spärrar'}</span>
                        </span>` : nothing}
                    ${advisory > 0 ? html`
                        <span class="sb-count advisory" data-mark="stepdrill-status:advisory">
                            <span aria-hidden="true">⚠</span>
                            <span class="sb-n">${advisory}</span>
                            <span class="sb-kind">${advisory === 1 ? 'upplysning' : 'upplysningar'}</span>
                        </span>` : nothing}
                    ${clean ? html`
                        <span class="sb-clean" data-mark="stepdrill-status:clean">
                            <span aria-hidden="true">✓</span> Inga anmärkningar
                        </span>` : nothing}
                    <span class="sb-spacer"></span>
                    ${clean ? nothing : html`
                        <uui-button
                            class="sb-toggle"
                            look="secondary"
                            compact
                            data-mark="stepdrill-status:toggle"
                            label=${this._statusOpen ? 'Dölj anmärkningarna' : 'Visa anmärkningarna'}
                            aria-expanded=${this._statusOpen ? 'true' : 'false'}
                            @click=${() => { this._statusOpen = !this._statusOpen; }}
                        >${this._statusOpen ? 'dölj ▴' : 'visa ▾'}</uui-button>`}
                </div>
                ${this._statusOpen && !clean ? html`
                    <div class="sb-details" data-mark="stepdrill-status:details">
                        ${notices.map((n) => html`
                            <div class="notice ${n.blocking ? 'blocking' : 'advisory'}">
                                <span class="notice-glyph" aria-hidden="true">${n.blocking ? '⛔' : '⚠'}</span>
                                <span>${n.body}</span>
                            </div>
                        `)}
                    </div>
                ` : nothing}
            </div>
        `;
    }

    /** The tool's totals; "—" while the formula row is not built, because nothing was computed and an
     *  unbuilt row is not "0 segment" (stepdrill-preview.test.ts, "a waiting formula row has one block"). */
    private _renderTotals() {
        const d = this.item?.detail;
        if (!d) return nothing;
        const waits = this._formulaRowUnbuilt;
        const shown = (text: string) => (waits ? '—' : text);
        return html`
            <div class="totals">
                <div>Totalt antal segment <strong>${shown(String(d.segmentCount))}</strong></div>
                <div>Total setup-tid (min) <strong>${shown(formatSv(d.totalSetupTime ?? 0, 1))}</strong></div>
                <div>Total maskin-tid (min) <strong>${shown(formatSv(d.totalMachineTime ?? 0, 1))}</strong></div>
                <div>Slipad profillängd (mm) <strong>${shown(formatSv(d.profileLength ?? 0, 1))}</strong></div>
            </div>
        `;
    }

    /**
     * The operation columns as a TRACK LIST, handed to the stylesheet as a custom property.
     *
     * <p><b>A property and not the whole template, and that distinction is the bug it fixes.</b> The
     * count is authored data, so something has to come from script — but writing the finished
     * `grid-template-columns` inline made it beat the container query that turns the table into
     * cards below 900px. The card layout then never applied, and the narrow view kept a
     * twelve-column desktop grid: "i mobilvyn går de sönder helt". A custom property is data; the
     * stylesheet stays in charge of which template uses it, and the card rule simply does not.</p>
     *
     * <p>Empty when no operation is configured, and an empty value in a track list is nothing —
     * the table is then exactly the shape it was before this feature.</p>
     */
    /**
     * One read-only operation cell per column — the house cell shape, so the card view labels it and
     * the desktop hides that label under the column head.
     *
     * <p>Written once because it was written four times: the segment row, the tip row, the shank row
     * and the totals row all emit exactly this, and the cell COUNT has to match the shared template
     * in every one of them. Four copies is four places for a row to fall a cell short, which is a
     * failure only a browser shows.</p>
     */
    private _renderOpsCells(value: (op: string, key: string) => string, extraClass = '') {
        return this._outcomeColumns.map((c) => c.keys.map((key) => html`
            <label class="cell readonly">
                <span class="cell-label">${c.op} ${measureLabel(key).toLowerCase()}</span>
                <span class="readonly-cell ops-cell ${extraClass}">${value(c.op, key)}</span>
            </label>`));
    }

    /**
     * How many columns precede the operation block — the count a spanning cell in the two-level head
     * and in the totals row has to skip.
     *
     * <p>Derived in ONE place because it has to agree with three others: the stylesheet's fixed track
     * list, the `no-transition` variant of it, and the trailing `span 2` for Resultat + remove. It was
     * written out twice, which is two places to forget when a fixed column is added.</p>
     */
    private get _fixedColumnCount(): number {
        // # · α · Ø · Tol. · L (+ Kona · r). The Tol. column arrived with stegborr-segmentmodellen 17.
        return this._showsTransitionColumns ? 7 : 5;
    }

    private _opsColumnTrack(outcomeCells: number): string {
        return outcomeCells > 0 ? `repeat(${outcomeCells}, minmax(52px, 68px))` : '';
    }

    /** How many measure columns the operations add. One reading for two answers: the track list
     *  they get, and whether the table has anything that scrolls — which is what freezes the
     *  columns either side of them (stegborr-segmentmodellen 28). */
    private get _opsCellCount(): number {
        return this._outcomeColumns.reduce((n, c) => n + c.keys.length, 0);
    }

    /**
     * The upper half of the two-level head: one cell per operation, spanning its own measures.
     *
     * <p>Rendered as a row of its own because the table is one flat grid — a spanning cell is
     * `grid-column: span n`, and the fixed input columns to its left are skipped by one spanning
     * blank. Absent when no operation is configured, and then the table looks exactly as it did.</p>
     */
    private _renderOpsHeaderRow(columns: { op: string; keys: string[] }[]) {
        if (columns.length === 0) return nothing;
        const fixedCells = this._fixedColumnCount;
        return html`
            <div class="row head ops-head ${this._rowVariant}">
                <span style="grid-column: span ${fixedCells};"> </span>
                ${columns.map((c) => html`
                    <span class="ops-head-name" style="grid-column: span ${c.keys.length};"
                          title="Operationen ${c.op} — dess mått står i kolumnerna under">${c.op}</span>
                `)}
                <span style="grid-column: span 2;"> </span>
            </div>
        `;
    }

    /**
     * The bottom of the table: what each operation cost over EVERY segment, and the tool's own total.
     *
     * <p><b>Two sources, and the difference between them is information.</b> The per-operation cells
     * are summed from the segments this table is showing; the cell under Resultat is the SERVER's
     * `totalSetupTime` + `totalMachineTime`. They can legitimately differ — a measure with no role
     * contributes to its column and to its segment, and to no grand total (operationsformen 04) — so
     * the row is where that gap becomes visible instead of being papered over by re-summing the
     * server's number out of the grid.</p>
     */
    private _renderColumnTotalsRow(columns: { op: string; keys: string[] }[]) {
        if (columns.length === 0) return nothing;
        const fixedCells = this._fixedColumnCount;
        const grand = Number(this.item?.detail?.totalSetupTime ?? 0)
            + Number(this.item?.detail?.totalMachineTime ?? 0);

        return html`
            <div class="row body ops-total ${this._rowVariant}">
                <span class="idx ops-total-label" style="grid-column: span ${fixedCells};">Σ operation</span>
                ${this._renderOpsCells(
                    (op, key) => formatSv(this._outcomeSums.get(op)?.get(key) ?? 0, 1),
                    'ops-total-cell')}
                <div class="result">
                    <div class="result-total" title="Verktygets totaler, från servern — inte en omsummering av tabellen ovan.">
                        Σ <strong>${formatSv(grand, 1)}</strong>
                    </div>
                </div>
                <span></span>
            </div>
        `;
    }

    private _renderHeader() {
        // Empty cells get a non-breaking space so they have a text-baseline.
        // Without it the border-bottom sits higher than the filled siblings
        // (min-height on grid-item spans isn't reliable across all render paths).
        const lengthLabel = this._lengthHeaderLabel();
        return html`
            <div class="row head ${this._rowVariant}">
                <span> </span>
                <span title="Steg 1: spetsvinkeln, INKLUDERAD (2α, t.ex. 118°) — spetsen är steg 1:s kon, och dess Kona-värde är spetslängden. Steg 2 och framåt: kon-vinkel in till cylindern, grader från verktygsaxeln (liten vinkel = lång, flack kon). Skaft: approach-vinkeln upp till skaftet. Steg 1 och Skaft ärver sin vinkel när cellen är tom — det ärvda värdet står som platshållare; ↺ återställer ett överstyrt.">α (°)</span>
                <span title="Diametern på den cylindriska delen för detta steg (vid den raka delen, efter konan)">Ø (mm)</span>
                <span title="Tomt = standardklassen (ISO 2768) · ett ±tal, t.ex. ±0,05 · en passningsklass, t.ex. k7 · två värden kon / cyl, t.ex. k7 / ±0,05.
Tolerans på stegets Ø. Tom cell = ritningshuvudets standard gäller (klassen ur konfiguratorns port) — det värdet står som platshållare. Två värden håller radens kon och cylinder till olika krav: konen kan borra där cylindern aldrig går ner i hålet. Meningsfull bara där steget borrar; på rader som inte borrar vid det satta borrdjupet är cellen dämpad men går att redigera.">Tol.</span>
                <span title=${lengthLabel.title}>${lengthLabel.text}${this._showsPosEcho() ? html`
                    <span
                        class="head-echo"
                        title="Efter varje värde: ⟨positionen från spetsen⟩ — härledd, inte inmatad."
                    >⟨→spets⟩</span>` : nothing}</span>
                ${this._showsTransitionColumns ? html`
                    <span title="Konan in TILL denna cylinder (axiell längd, beräknad från Δdiameter och α). Visas som referens — räknas in i Total drillängd automatiskt.">Kona (mm)</span>
                    <span title="Innerradie på övergången — referens för verktygsval/validering. Lämna tomt om okänt.">r (mm)</span>
                ` : nothing}
                ${this._outcomeColumns.map((c) => c.keys.map((key) => html`
                    <span class="ops-measure"
                          title="${c.op} – ${measureLabel(key)}, minuter för DETTA segment">${measureLabel(key)}</span>
                `))}
                <span class="head-result"
                      title="Setup-tid + maskin-tid summerat över alla operationer för detta segment.">Resultat (min)</span>
                <span> </span>
            </div>
        `;
    }

    /**
     * The α cell of a row whose angle INHERITS when left empty — one shape for the two cones that
     * have a default behind them (stegborr-segmentmodellen 19 — spetsen är segment 1:s kon, "ett
     * mönster, två koner"): row 1's point angle (the PointAngle formula's value) and the shank row's
     * approach angle (the admin default). The rules, and they are the same for both:
     *
     *   · EMPTY = INHERIT. The cell stores nothing until the user types; the resolved default is
     *     never written into the segment or the override input, so it follows the admin setting
     *     when that changes, and a deliberate 140 can never be mistaken for a seeded one.
     *   · The inherited value shows as the PLACEHOLDER — the row never looks empty, and the engine
     *     computes with the resolved value whether or not anything is typed.
     *   · An overridden value is MARKED with a reset glyph (↺) inside the field, which clears the
     *     cell; Backspace to empty does the same. The glyph is the carrier, not a colour.
     *   · A declared bound on the column (`StepdrillSegmentRule`, or the shank's admin min/max) is
     *     shown exactly as on any other cell: the issue marks the field and its message leads the
     *     title; the bound's guidance rides in the title, because the placeholder slot is taken by
     *     the inherited value.
     *
     * A seed or preset that states the shank angle (StepdrillSeedProposer's `{alias}._approachAngle`
     * companion, measured off the drawing sheet) makes the shank row show as OVERRIDDEN with a ↺ —
     * and that is correct, not a bug to "fix": a sheet-measured angle is a genuine override of the
     * admin default for this evaluation, and the ↺ is how a person hands it back.
     *
     * The reset button rides in uui-input's `append` slot ON PURPOSE: `.cell` is display:contents
     * on the desktop grid, so a sibling of the input would be a grid child of its own and shift
     * every column after it — the trap the file's history records for the remove button. The
     * convention marker (`2α`) rides in the `prepend` slot for the same reason.
     */
    private _renderInheritedAngleCell(o: {
        key: string;
        cellLabel: string;
        fieldLabel: string;
        /** The angle-convention marker shown INSIDE the field (`2α` on row #1); absent = unmarked. */
        mark?: string;
        /** What the cell shows when nothing is typed — the resolved value, from the server. */
        inherited: number;
        /** The stored override, or null when the row inherits. */
        override: number | null;
        /** Raw text while the user is mid-edit (kept verbatim so trailing decimals survive). */
        editing: string | undefined;
        placeholderPrefix: string;
        /** Drop the degree sign from the PLACEHOLDER (never from the guidance, which has room for
         *  it). Row #1's field carries the `2α` marker in its prepend slot, and "2α" + "spets 140°"
         *  did not fit the α column at any width the grid can afford — neither the marker nor the
         *  angle was readable. Found while building stegborr-segmentmodellen 22 — toleransen per
         *  feature; it belongs to stegborr-segmentmodellen 19 — spetsen är segment 1:s kon, which is
         *  what put the marker there. The title already says the cell is the point angle, so the
         *  placeholder is just the number. */
        placeholderBare?: boolean;
        title: string;
        /** The live bound a declared rule puts on this column, as `_cellBoundText` phrases it. */
        bound?: string;
        issue?: string;
        severity?: 'blocking' | 'advisory';
        onInput: (e: Event) => void;
        onBlur: () => void;
        onReset: () => void;
    }) {
        const overridden = o.override !== null && o.override > 0;
        // Compact, not two fixed decimals: an angle reads as "118" or "30,5", never "118,00".
        const value = o.editing !== undefined ? o.editing : overridden ? compactSv(o.override!) : '';
        const inheritedText = `${compactSv(o.inherited)}°`;
        const guidance = [
            o.title,
            o.bound === undefined ? undefined : `Tillåtet: ${o.bound}`,
            overridden
                ? `Överstyrt — ärvt värde ${inheritedText}. ↺ eller tom cell återställer.`
                : `Ärvt ${inheritedText} — skriv ett tal för att överstyra.`,
        ].filter((line) => line !== undefined).join('\n');
        return html`
            <label
                class="cell"
                data-key=${o.key}
                data-angle-source=${overridden ? 'overridden' : 'inherited'}
                data-severity=${o.severity ?? nothing}
            >
                <span class="cell-label">${o.cellLabel}</span>
                <uui-input
                    type="text"
                    inputmode="decimal"
                    placeholder="${o.placeholderPrefix}${o.placeholderBare === true ? compactSv(o.inherited) : inheritedText}"
                    label=${o.fieldLabel}
                    ?error=${o.issue !== undefined}
                    title=${o.issue === undefined ? guidance : `${o.issue}\n${guidance}`}
                    .value=${value}
                    @input=${o.onInput}
                    @blur=${o.onBlur}
                >${o.mark === undefined ? nothing : html`
                    <span
                        slot="prepend"
                        class="angle-mark"
                        data-mark="stepdrill-cell:angle-mark"
                        aria-hidden="true"
                    >${o.mark}</span>`}${overridden ? html`
                    <button
                        slot="append"
                        type="button"
                        class="reset-override"
                        data-mark="stepdrill-cell:reset"
                        title="Återställ till ärvt värde (${inheritedText})"
                        aria-label="Återställ ${o.fieldLabel} till ärvt värde ${inheritedText}"
                        @click=${o.onReset}
                    >↺</button>` : nothing}</uui-input>
            </label>
        `;
    }

    /**
     * Row 1's α: the INCLUDED point angle (118° reads as 118°, not 59°), which the engine already
     * treats as such (`tipLength = d/2 · tan((180 − α)/2)`). Stored on the segment as
     * `incomingAngle`; 0 there means INHERIT — the server resolves the PointAngle formula when row
     * 0's angle is not positive, and the presets' first step carries `incomingAngle: 0` with that
     * meaning. The resolved value comes back as `detail.tipAngle`.
     */
    private _renderPointAngleCell(seg: SegmentInput) {
        const i = POINT_ANGLE_ROW;
        const stored = Number(seg.incomingAngle ?? 0);
        return this._renderInheritedAngleCell({
            key: 'incomingAngle',
            cellLabel: 'α spets',
            fieldLabel: 'Steg 1 spetsvinkel (inkluderad 2α, grader)',
            mark: '2α',
            inherited: Number(this.item?.detail?.tipAngle ?? 0),
            override: stored > 0 ? stored : null,
            editing: this._editing.get(`${i}:incomingAngle`),
            // Just the number: the `2α` marker sits in the same field, and "2α" + "spets 140°" left
            // neither readable in the α column (stegborr-segmentmodellen 19 — spetsen är segment 1:s
            // kon). The title carries "spets", the marker carries the convention.
            placeholderPrefix: '',
            placeholderBare: true,
            title: 'Spetsvinkel, INKLUDERAD (2α) — övriga raders α mäts från axeln.',
            // The same wire every other α cell reads: a rule bound on `incomingAngle` for this row
            // guides the cell, and a break marks it. The inherit pattern changes what the
            // placeholder carries, not whether the rule reaches the cell.
            bound: this._cellBoundText(i, 'incomingAngle'),
            issue: this._cellIssue(i, 'incomingAngle'),
            severity: this._cellSeverity(i, 'incomingAngle'),
            onInput: this._onNumberInput(i, 'incomingAngle'),
            onBlur: this._onNumberBlur(i, 'incomingAngle'),
            onReset: this._onPointAngleReset,
        });
    }

    /**
     * The Tol. cell (stegborr-segmentmodellen 17 — toleransnivåer, beslut 3): the drawing's own
     * tolerance level per dimension, free text, beside the Ø it qualifies.
     *
     *   · EMPTY = the STANDARD applies — the drawing header's UNLESS OTHERWISE SPECIFIED, which is
     *     the port's ISO 2768 class. The standard's figure for this row's Ø shows as the PLACEHOLDER
     *     (the same pattern as the inherited α cells: the row never looks empty, nothing is stored).
     *   · `±0,05` / `0,05` = the row's OWN value; `k7` / `h6` = a fit class the server resolves in the
     *     authored fit table. The server parses; this cell carries the text verbatim, Swedish comma
     *     included.
     *   · A row where NEITHER feature drills is DIMMED, never hidden or disabled — the title says why
     *     in words, and `data-tolerance-applies` says it for the DOM (colour never carries a state
     *     alone). `data-tolerance-source` echoes the server's reading so a test can see the level
     *     without parsing the cell.
     *   · A fit CLASS's resolved ZONE is in the title, not in new columns: `k7: +0,001 … +0,013
     *     (zonbredd 0,012 mm)`. Only the class: a fit is asymmetric — both deviations above nominal
     *     for a k-fit — and `k7` is a name the cell cannot be read for a number, while a ±0,05 IS its
     *     own zone and spelling it "-0,05 … +0,05" tells the author what they typed. Two more grid
     *     columns would cost every row's width to show what is asked for only when a number
     *     surprises them.
     *
     * No `_editing` stash: the value IS the raw text, so re-rendering from the draft cannot reformat
     * a half-typed "±0,0" the way the number cells' formatter would.
     */
    private _renderToleranceCell(seg: SegmentInput, i: number) {
        // NORMALISED at the boundary: a wire field that means "no tolerance" arrives as an absent
        // property OR as an explicit null, depending on what the server's serializer was told, and
        // everything below this line is written against ONE of those. `?? undefined` picks it, once,
        // where the value enters — rather than each of the half-dozen tests below having to guess
        // which shape it got. Without it, `cone !== undefined` was true for a null cone and the
        // `cone.source` behind it threw, which in a Lit render is not a wrong cell but NO TABLE:
        // the exception escapes `render()` and the whole preview stays blank. A render must never
        // throw on a nullable wire field — there is no partial failure here to fall back to.
        const tol = this.item?.detail?.segments?.[i]?.tolerance ?? undefined;
        const cone = this.item?.detail?.segments?.[i]?.coneTolerance ?? undefined;
        const applies = tol?.applies ?? true;
        const standard = typeof tol?.standard === 'number' ? `±${compactSv(tol.standard)}` : undefined;
        // The resolved zone, and ONLY for a fit CLASS. A ± level — the row's own value, or the
        // standard's — is symmetric by construction: its zone is the number already in the cell (or
        // in the placeholder) written twice with two signs, so a line spelling it out repeats what
        // the author is looking at. A fit class is the case where the zone says something the cell
        // cannot: `k7` is a name, its two deviations sit wholly above nominal, and nothing on screen
        // carries them.
        //
        // `width` is READ, never recomputed. It travels on the wire beside `lower`/`upper` (see
        // SegmentToleranceResult) precisely so one arithmetic — `(upper − lower)` — has one home, on
        // the server, where `SegmentTolerance` derives it. A local `?? upper - lower` fallback would
        // be a second implementation that agrees today and is the thing that disagrees later. The
        // four keys stand or fall together, so a payload carrying two of them and not the third is
        // not a case to paper over: no zone line, the same as no zone at all.
        //
        // ONE spelling of a resolved zone, asked twice: the row's line wants the width tail, a
        // per-feature half does not have room for it. Written out twice, the two drifted the moment
        // `deviationSv` changed for one of them.
        const classZone = (t: SegmentToleranceResult | undefined, withWidth = false) => {
            if (t?.source !== 'klass' || typeof t.lower !== 'number' || typeof t.upper !== 'number')
                return undefined;
            const deviations = `${t.class ? `${t.class}: ` : ''}${deviationSv(t.lower)} … ${deviationSv(t.upper)}`;
            if (!withWidth) return deviations;
            return typeof t.width === 'number'
                ? `${deviations} (zonbredd ${preciseSv(t.width)} mm)`
                : undefined;
        };
        const zone = classZone(tol, true);
        // The two features' readings, side by side, and ONLY when they differ (stegborr-segmentmodellen
        // 22 — toleransen per feature). A single-value cell holds both features to the same figure, and
        // spelling that out twice would be noise on every row in the corpus; two values are the case
        // where the cell text alone (`k7 / ±0,05`) does not say what either half resolved to.
        // A ± level IS its own zone, so a half that is not a fit class says the number back.
        const featureZone = (t?: SegmentToleranceResult) =>
            classZone(t) ?? (typeof t?.value === 'number' ? `±${compactSv(t.value)}` : undefined);
        // The server sends `coneTolerance` only when the cell SPLIT, so absent is "both features read
        // the same figure". The differs-check stays for a payload from a server that sent both.
        const splitCell = cone !== undefined && tol !== undefined
            && (cone.source !== tol.source || (cone.class ?? '') !== (tol.class ?? '') || cone.value !== tol.value);
        const perFeature = splitCell
            ? `kon ${featureZone(cone) ?? '—'} · cyl ${featureZone(tol) ?? '—'}`
            : undefined;
        // What the cell ACCEPTS, in the cell's own title as well as the column header's: the placeholder
        // can only show the standard's value, so nothing on screen said that a class — or two values —
        // was allowed at all (stegborr-segmentmodellen 22 — toleransen per feature, the UX finding
        // "Cellen säger inte vad den tar").
        const standardClass = tol?.source === 'standard' && tol.class ? `-${tol.class}` : '';
        // The dimmed cell says WHY in words, with the depth that decided it — grey alone was a state
        // nobody could read (stegborr-segmentmodellen 22 — toleransen per feature, the UX finding
        // "Dämpningen förklarar sig inte").
        const depth = Number(this.item?.detail?.drillDepth ?? 0);
        const guidance = [
            `Tomt = standardklassen (ISO 2768${standardClass}) · ett ±tal, t.ex. ±0,05 · en passningsklass, t.ex. k7 · två värden kon / cyl, t.ex. k7 / ±0,05`,
            perFeature,
            perFeature === undefined ? zone : undefined,
            standard !== undefined
                ? `Standard för den här Ø${tol?.class ? ` (klass ${tol.class})` : ''}: ${standard}`
                : 'Ingen standardtolerans för den här Ø — den ligger utanför ISO 2768:s intervall, eller är inte ifylld.',
            applies ? undefined
                : depth > 0
                    ? `Ingen del av segmentet borrar vid borrdjup ${compactSv(depth)} mm — toleransen används inte. Cellen är dämpad men går att redigera.`
                    : 'Ingen del av segmentet borrar vid det satta borrdjupet — toleransen används inte. Cellen är dämpad men går att redigera.',
        ].filter((line) => line !== undefined).join('\n');
        const issue = this._cellIssue(i, 'tolerance');
        return html`
            <label
                class="cell"
                data-key="tolerance"
                data-tolerance-applies=${applies ? 'true' : 'false'}
                data-tolerance-source=${tol?.source ?? nothing}
                data-severity=${this._cellSeverity(i, 'tolerance') ?? nothing}
            >
                <span class="cell-label">Tol.</span>
                <uui-input
                    type="text"
                    placeholder=${standard ?? 'Tol.'}
                    label="Segment ${i + 1} tolerans (±mm, passningsklass, eller kon / cyl)"
                    ?error=${issue !== undefined}
                    title=${issue === undefined ? guidance : `${issue}\n${guidance}`}
                    .value=${seg.tolerance ?? ''}
                    @input=${(e: Event) => this._editField(i, 'tolerance', (e.target as HTMLInputElement).value)}
                ></uui-input>
            </label>
        `;
    }

    /** Editable shank row at the BOTTOM of the table: the tier-3 end-user override of the
     *  shank-approach angle (the implicit final taper up to the shank Ø). Ø + L are read-only
     *  context (both formula-derived). data-segment-index="shank" + data-key="approachAngle"
     *  let the silhouette's approach-angle click route here. Hidden when there's no shank. */
    private _renderShankRow() {
        const d = this.item?.detail;
        const terminalDia = Number(d?.terminalDiameter ?? 0);
        if (terminalDia <= 0) return nothing;
        const shankLen = Number(d?.shankLength ?? 0);
        const min = Number(d?.approachAngleMin ?? 0);
        const max = Number(d?.approachAngleMax ?? 0);
        const range = min > 0 && max > 0 ? ` (${formatSv(min, 0)}–${formatSv(max, 0)}°)` : '';
        return html`
            <div class="row body shank-row ${this._rowVariant}" data-segment-index=${SHANK_INDEX}>
                <span class="idx" data-mark="stepdrill-seg:tone" style=${this._rowTone(SHANK_INDEX)}>Skaft</span>
                ${this._renderInheritedAngleCell({
                    key: SHANK_KEY,
                    cellLabel: 'α',
                    fieldLabel: `Skaft-approachvinkel (grader)${range}`,
                    inherited: Number(d?.approachAngle ?? 0),
                    // The typed value until the echo lands, else the persisted override.
                    override: this._approachDraft !== undefined ? this._approachDraft : this._approachOverride(),
                    editing: this._editing.get('shank:approachAngle'),
                    placeholderPrefix: '',
                    title: `Approach-vinkel: konan upp till skaftet (grader från axeln${range}). Tom cell ärver admin-defaulten.`,
                    onInput: this._onApproachAngleInput,
                    onBlur: this._onApproachAngleBlur,
                    onReset: this._onApproachAngleReset,
                })}
                <span class="readonly-cell" title="Skaft-/terminaldiameter (formel)">Ø ${formatSv(terminalDia, 2)}</span>
                <span class="readonly-cell" data-key="tolerance" title="Skaftet borrar inte — ingen tolerans att ange här.">—</span>
                <span class="readonly-cell" title="Skaftlängd (formel)">${shankLen > 0 ? formatSv(shankLen, 2) : '—'}</span>
                ${this._showsTransitionColumns ? html`
                    <span class="readonly-cell">—</span>
                    <span class="readonly-cell">—</span>
                ` : nothing}
                ${this._renderOpsCells(() => '—')}
                <div class="result"><div class="result-total row-name">Skaft</div></div>
                <span></span>
            </div>
        `;
    }

    render() {
        // An Error with NO payload (evaluator crash before producing state) has nothing to
        // edit — the bare error is all there is. With a Detail present (e.g. the runtime's
        // cycle-error overlay on an otherwise evaluated item, issue 09) the host renders the
        // error banner and THIS component must keep rendering the editor: it holds exactly
        // the inputs that lift the error.
        if (this.item?.status === 'Error' && !this.item?.detail) {
            return html`<div class="error">${this.item.errorMessage ?? 'Verktyget kunde inte utvärderas.'}</div>`;
        }
        // ONE read of the real rows per render. `_segments` resolves through `_serverSegments`,
        // which maps the server payload into N fresh objects per call — cheap today because the
        // draft short-circuits it, but the local is what keeps it that way. Master had exactly this
        // local; the placeholder work split it into three reads, and the next person to reset the
        // draft would have turned every keystroke into three N-row maps.
        const real = this._segments;
        const segments = this._displaySegments;
        const ops = this._operationNames;
        // REAL rows, not the drawn ones. The placeholder on a one-diameter tool is display only, and
        // everything downstream of this flag is about whether the tool HAS something: the STEP
        // export's enablement, and the "no operations configured" hint. Counting the placeholder
        // would offer an export of nothing and put a warning on a tool nobody has touched — issue
        // 16's rule, broken by a convenience.
        const hasSegments = real.length > 0;
        // Empty-ops state: power-user added the Stepdrill block but hasn't
        // configured any operations in backoffice yet. Surface a clear hint
        // so the end-user doesn't think they're looking at broken zero-tids.
        const opsMissing = hasSegments && ops.length === 0;
        const currentMode: StepdrillMeasurementMode = this.item?.detail?.measurementMode ?? 'Relative';
        const shankError = this.item?.detail?.shankDiameterError;
        // TWO sources, one list of bands.
        //
        //   · ItemState.Warnings — the item-level verdicts (drill depth unset, total length too
        //     short, inverted angle range). Author-facing: several name an authored formula, so the
        //     leaf strips their prose under production mode and they simply vanish there.
        //   · detail.segmentIssues — the ROW-addressed ones. Customer-safe by construction, so they
        //     survive production mode, and they must be READABLE there: marking a cell and putting
        //     the reason in a `title` leaves a customer with a box and no way to act on it — hover
        //     does not exist on a touch screen. That was the whole argument for letting the marks
        //     through in the first place, and it applies one level down too.
        //
        // Deduped on the message: in DESIGN mode a geometry break is both a warning and a segment
        // issue with identical text, and printing it twice reads as two problems.
        // The ROW version wins where both exist. A rule break books both — a warning, because that
        // is what the commercial gate reads, and a row issue, because that is what marks the cell —
        // and they carry the SAME text by construction. Printing both reads as two problems; keeping
        // the row one means the band can say WHICH step, derived from the issue's own index rather
        // than from a prefix baked into one of the two strings.
        const rowIssues = this.item?.detail?.segmentIssues ?? [];
        const rowMessages = new Set(rowIssues.map((issue) => issue.message));
        const itemWarnings = (this.item?.warnings ?? [])
            .filter((w) => !!w.message && !rowMessages.has(w.message!));
        const rowWarnings = rowIssues.map((issue) => ({
            message: `Steg ${issue.segmentIndex + 1}: ${issue.message}`,
            severity: issue.severity,
        }));
        const warnings = [...itemWarnings, ...rowWarnings];
        // R6: collapsed/undimensioned segments the server flagged incomplete (AwaitingInput). Distinct
        // from advisory warnings — these BLOCK a complete drill; the customer must enter a real length.
        const incompleteSegments: number[] = this.item?.detail?.incompleteSegments ?? [];
        const fromFormulas = this._segmentsFromFormulas;
        const formulaRowUnbuilt = this._formulaRowUnbuilt;
        const addRefusal = this._addRefusal;
        // While the row is not built the note is the server's own blocking sentence, which names what waits
        // (a formula or Formlerna gäller när) and on what, or which formula failed; the detail carries
        // nothing else that tells them apart.
        const waitingNote = itemWarnings.filter((w) => w.severity === 'Blocking').map((w) => w.message).join(' ')
            || (this._formulaRowFails
                ? 'Segmentet räknas när formelfelet är rättat.'
                : 'Segmentet väntar och kan inte ändras här.');
        // ONE list of verdicts, in the order a reader meets them, handed to the status bar. The
        // three sources kept their own band above the grid before issue 11; they are the same
        // three sentences, only somewhere that cannot push the table around.
        // The item's own error stops the tool, so it is a spärr here too, beside the banner above.
        const itemErrors = this.item?.status === 'Error' && this.item?.errorMessage
            ? this.item.errorMessage.split(' | ')
            : [];
        const notices: StatusNotice[] = [
            ...itemErrors.map((message) => ({
                blocking: true,
                body: html`<span class="notice-kind">Spärrar realisering:</span> ${message}`,
            })),
            ...(shankError ? [{
                blocking: false,
                body: html`<span class="notice-kind">Upplysning:</span> Skaftdiameter-formel: ${shankError} — sista segmentets <code>diameterDelta</code> behandlas som 0 tills formeln rättats.`,
            }] : []),
            ...warnings.map((w) => ({
                blocking: w.severity === 'Blocking',
                body: html`<span class="notice-kind">${w.severity === 'Blocking' ? 'Spärrar realisering:' : 'Upplysning:'}</span> ${w.message}`,
            })),
            ...(incompleteSegments.length ? [{
                blocking: true,
                // Under formulas the customer has no length to type, so the instruction goes and the
                // fact stays.
                body: html`<span class="notice-kind">Ofullständigt:</span> steg ${incompleteSegments.map((i) => i + 1).join(', ')} saknar mått (höjd 0)${fromFormulas ? '.' : ' — ange en längd för ett komplett borr.'}`,
            }] : []),
        ];
        return html`
            <div class="header">
                <div class="header-left">
                    <span class="alias">${this._isSingleSegmentTool ? 'Borr' : 'Stegborr'} · ${this.item.alias}</span>
                    <!-- Shown on a one-step tool too: there Pos→spets counts the tip in, so the
                         reference still changes the figure. Not under formulas: the length is
                         read in the block's mode, and the engine reads no _mode then. -->
                    ${fromFormulas ? nothing : html`<select
                        class="mode-select-native"
                        @change=${this._onModeChange}
                        title="Vad betyder L-värdet?"
                        aria-label="Mätreferens"
                    >
                        <option value="Relative" ?selected=${currentMode === 'Relative'}>L = cylindrisk längd</option>
                        <option value="AbsoluteFromTip" ?selected=${currentMode === 'AbsoluteFromTip'}>L = pos från spets</option>
                        <option value="AbsoluteFromStep1" ?selected=${currentMode === 'AbsoluteFromStep1'}>L = pos från Steg 1</option>
                    </select>`}
                </div>
                ${ops.length > 0 ? html`
                    <span class="op-names" title="Operationer som beräknas per segment">
                        Operationer: ${ops.join(', ')}
                    </span>
                ` : nothing}
            </div>
            <div class="preview-layout">
                <div class="preview-body">
                    ${opsMissing ? html`
                        <div class="ops-missing">
                            Inga operationer konfigurerade — kontakta admin för att lägga till operationer i verktygsblocket.
                        </div>
                    ` : nothing}
                    ${segments.length === 0 && fromFormulas && !formulaRowUnbuilt
                        ? nothing
                        : segments.length === 0 && !formulaRowUnbuilt
                        ? html`
                            <div class="empty-guide">
                                <div class="empty-guide-title">Bygg ditt verktyg</div>
                                <ul class="empty-guide-bullets">
                                    <li>En rad per <strong>cylindrisk del</strong> på borren</li>
                                    <li>Spetsen är <strong>Steg 1:s kon</strong>: dess längd räknas fram från diametern på Steg 1 och spetsvinkeln och står i Steg 1:s Kona-kolumn</li>
                                    <li><strong>Steg 1:s α ÄR spetsvinkeln</strong> (inkluderad 2α, t.ex. 140°) — lämna tomt för att ärva konstruktörens formel-värde; det ärvda står som platshållare. Steg 2 och framåt: <strong>α</strong> = kon-vinkeln in till cylindern, från axeln</li>
                                </ul>
                                <div class="empty-guide-example">
                                    <div class="empty-guide-example-title">Exempel — läs så här från en ritning:</div>
                                    <div class="empty-guide-example-row">
                                        <span class="empty-guide-example-label">Cylinder Ø10 mm, 12 mm lång, spetsvinkel 140°</span>
                                        <span class="empty-guide-example-arrow">→</span>
                                        <span class="empty-guide-example-value">α=140, Ø=10, L=12</span>
                                    </div>
                                    <div class="empty-guide-example-row">
                                        <span class="empty-guide-example-label">Kona 30° upp till Ø14, sedan 8 mm cylinder</span>
                                        <span class="empty-guide-example-arrow">→</span>
                                        <span class="empty-guide-example-value">α=30, Ø=14, L=8</span>
                                    </div>
                                </div>
                            </div>
                        `
                        : html`
                            <div
                                class="segments-grid ${this._rowVariant} ${this._opsCellCount > 0 ? 'has-ops' : ''}"
                                style="--ops-cols: ${this._opsColumnTrack(this._opsCellCount)};"
                                @scroll=${this._onGridScroll}
                                @focusin=${this._onSegmentFocus}
                                @focusout=${this._onSegmentBlur}
                            >
                                ${this._renderOpsHeaderRow(this._outcomeColumns)}
                                ${this._renderHeader()}
                                ${formulaRowUnbuilt
                                    ? this._renderWaitingFormulaRow()
                                    : segments.map((seg, i) => fromFormulas
                                        ? this._renderFormulaSegment(seg, i)
                                        : this._renderSegment(seg, i))}
                                ${this._renderShankRow()}
                                ${this._renderColumnTotalsRow(this._outcomeColumns)}
                            </div>
                        `}
                    <div class="action-row">
                        ${fromFormulas
                            ? html`<span class="formula-note" data-mark="stepdrill-seg:formula-note">
                                  ${formulaRowUnbuilt ? waitingNote : 'Segmentet bestäms av kalkylatorn och kan inte ändras här.'}
                              </span>`
                            : canAddSegment(segments.length, this._maxSegments)
                            ? html`
                                <uui-button
                                    class="add-btn"
                                    look="secondary"
                                    label="+ Lägg till segment"
                                    ?disabled=${addRefusal !== null}
                                    @click=${this._add}
                                ></uui-button>
                                ${addRefusal !== null
                                    ? html`<span class="add-blocked" data-mark="stepdrill-seg:add-blocked">${addRefusal}</span>`
                                    : nothing}
                            `
                            : nothing}
                        ${this.state?.customerMode ? nothing : html`
                            <uui-button
                                class="export-btn"
                                look="secondary"
                                label="⬇ STEP-fil"
                                title="Ladda ner 3D-modell (.stp) — kräver Umbraco-inloggning"
                                ?disabled=${!hasSegments}
                                @click=${this._exportStep}
                            ></uui-button>
                        `}
                    </div>
                    ${this._renderPartStrip()}
                    ${this._renderTotals()}
                    ${this._renderStatusBar(notices)}
                </div>
                <!-- No drawing while the formula row is not built: the tool it would draw has no segment. -->
                <ixit-rotary-tool-silhouette
                    .svg=${formulaRowUnbuilt ? '' : this.item?.silhouetteSvg ?? ''}
                    .holeSvg=${formulaRowUnbuilt ? '' : this.item?.projectionSvgs?.hole ?? ''}
                    .emptyText=${stoppedProfileText(this.item?.detail?.stoppedAt, this.item?.detail?.waitingOn) ?? (fromFormulas
                        ? 'Profil visas när segmentet är avgjort'
                        : 'Profil visas när du lagt till första segmentet')}
                    .defaultProjection=${this.state?.defaultProjection ?? 'tool'}
                    .activeSeg=${this._activeSeg}
                    .activeMeasure=${this._activeMeasure}
                    .isMeasureEditable=${this._isMeasureEditable}
                    @dof-measure-click=${this._onMeasureClick}
                ></ixit-rotary-tool-silhouette>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ixit-stepdrill-preview': IxitStepdrillPreview;
    }
}
